/**
 * Recording store — manages MediaRecorder state globally.
 *
 * Lives in Zustand (outside React tree) so the recording survives
 * navigation between pages.
 *
 * Note-first flow: pressing Record acquires the microphone, then immediately
 * creates a note draft, so the doctor can interact with it (link patient, set
 * clinic, add title) while recording. When recording stops, audio uploads to
 * that existing note.
 *
 * The microphone comes first deliberately. Creating the note ahead of
 * `getUserMedia` stranded a note server-side on every mic failure — it sat in
 * the list claiming to be recording, forever, with nothing captured into it.
 *
 * The store keeps the full note object from the create call — not just the
 * ID — so the detail pane can render instantly without a second API round-trip.
 */

import { create } from 'zustand'
import { api, ApiError } from '@/api/client'
import {
  APPEND_RETRIES,
  APPEND_RETRY_DELAY_MS,
  MAX_CONSECUTIVE_CHUNK_FAILURES,
  classifyAppendFailure,
} from '@/lib/append-failure'
import { SegmentRoller } from '@/lib/segmentRoller'
import { CaptureHeartbeat, trackIsLive } from '@/lib/capture-heartbeat'
import { holdWakeLock } from '@/lib/wake-lock'
import { trackClientEvent } from '@/lib/telemetry'
import { MicLossReporter, type MicLossDetectedBy } from '@/lib/mic-loss'
import { noteCapMessage } from '@/api/subscription-hooks'
import { useRecordingPrefs } from '@/stores/recordingPrefs'
import { toast } from '@/stores/toast'
import {
  savePendingRecording,
  getPendingRecording,
  getPendingRecordings,
  updatePendingStatus,
  deletePendingRecording,
  type PendingNoteRecording,
} from '@/lib/note-recording-db'
import type { Note } from '@/api/types'
import { confirm as confirmDialog } from '@/components/shared/ConfirmDialog'
import { discardRecordingCopy } from '@/lib/discard-recording-copy'
import {
  RECORDING_CAPTURE_ABANDONED,
  RECORDING_DRAFT_DISCARDED,
  type RecordingCaptureAbandonedDetail,
  type RecordingDraftDiscardedDetail,
} from '@/lib/recording-draft-discarded'
import { consultPadHasContent } from '@/lib/consult-pad'
import {
  SILENCE_FLOOR_DBFS,
  SILENCE_POLL_MS,
  SILENCE_WINDOW_SAMPLES,
  SilenceClock,
  rmsDbfs,
} from '@/lib/silence-clock'
import { clearSilenceAlert, raiseSilenceAlert } from '@/lib/silence-alert'
import {
  parseOtherLiveRecordings,
  reconcileDismissed,
  type LiveRecordingSummary,
} from '@/lib/run-on'
import {
  STOPPED_REASON_DURATION_CEILING,
  STOPPED_REASON_SILENCE_TIMEOUT,
  hasReachedRecordingCeiling,
  shouldCheckForSilence,
  toStoppedReason,
  type StoppedReason,
} from '@/lib/recording-ceiling'

type RecordingState = 'idle' | 'recording' | 'paused' | 'processing'

/**
 * A recorded consult whose audio is safe in IndexedDB but whose commit has not
 * succeeded (CLA-938). Projected from the DB rows so the UI never has to hold a
 * multi-megabyte Blob in React state.
 */
export interface PendingUpload {
  /** IndexedDB row id — the handle for retry / discard. */
  id: number
  noteId: number | null
  durationSeconds: number
  recordedAt: string
  attempts: number
  /**
   * `retryable` — a transport or server failure; "Retry upload" is offered.
   * `plan_capped` — a 402 note_cap_reached. The audio is fine, the plan is not.
   * Retrying just 402s again until the plan changes, so the UI prompts an
   * upgrade instead of a retry. Splitting these was the point of CLA-938's
   * note-cap branch: they used to share one `set()` with genuine failures.
   * `unreadable` — the browser can no longer read the audio back. Also not
   * retryable, and unlike the cap there is no way forward at all — the honest
   * thing is to say so rather than leave a button that cannot work (CLA-1243).
   */
  reason: 'retryable' | 'plan_capped' | 'unreadable'
  message: string
  /** True while a retry is in flight, so the button can disable itself. */
  retrying: boolean
}

/** Project a DB row into the UI-facing summary (drops the Blob). */
function toPendingUpload(row: PendingNoteRecording): PendingUpload | null {
  if (row.id == null) return null
  return {
    id: row.id,
    noteId: row.noteId,
    durationSeconds: row.durationSeconds,
    recordedAt: row.createdAt,
    attempts: row.attempts,
    reason:
      row.status === 'plan_capped'
        ? 'plan_capped'
        : row.status === 'unreadable'
          ? 'unreadable'
          : 'retryable',
    message:
      row.lastError ??
      // A row still marked `uploading` means the tab died mid-commit; it is an
      // orphan, not an in-flight upload, and it reads as one to the clinician.
      (row.status === 'uploading'
        ? 'Upload was interrupted before it finished.'
        : 'Upload did not complete.'),
    retrying: false,
  }
}

interface GenerateResult {
  noteId: number
  noteText: string
  transcript: string
  detectedTreatments: string[]
  isSuggestion: boolean
  /** The doctor's typed text at the moment Stop was pressed — used to restore the textarea */
  doctorText: string
}

/** Context passed when starting a consultation from an appointment */
export interface AppointmentContext {
  appointmentId: number
  patientUuid?: string
  patientName?: string
  appointmentType: string
  clinicId: number
  profileId?: number
}

/** Map appointment_type to the closest clinical_type for AI note generation */
function mapAppointmentTypeToClinicalType(appointmentType: string): string {
  switch (appointmentType) {
    case 'follow_up': return 'follow_up'
    case 'procedure': return 'procedure'
    case 'screening': return 'auto'
    case 'consultation': return 'auto'
    default: return 'auto'
  }
}

export type CaptureLossReason = 'muted' | 'ended' | 'error'

interface RecordingStore {
  state: RecordingState
  elapsed: number
  error: string | null
  /** Live warning: the mic has produced near-silence for several seconds while
   *  recording — likely muted or held by another app (e.g. Heidi). Self-clears
   *  when audio returns. Distinct from `error` (terminal); a non-blocking nudge. */
  audioWarning: boolean
  /**
   * The microphone stopped delivering audio mid-consult and has not come back
   * (CLA-1341). `muted` is what an iPhone browser does when the screen locks
   * or the clinician switches apps — the page stays alive, the recorder still
   * says "recording", the timer would keep counting. `ended` is the device
   * being revoked outright; `error` is the recorder itself failing. While set,
   * the timer is frozen at what was actually captured, so the number on
   * screen is honest. Self-clears when the track comes back (`muted` only);
   * `ended`/`error` end when the clinician finishes or discards.
   */
  captureLost: CaptureLossReason | null
  /**
   * The segment roller is recording this consult right now (CLA-1552): the
   * server's `segment_roller` flag is on, the consult isn't paused, and the
   * roller's own clone of the mic is delivering. It runs on its own recorder,
   * so when the main recorder dies under a live mic (`captureLost` set) the
   * roller is still capturing, and the clinician is told recording continues
   * on backup. Always false with the flag off — today's behaviour.
   */
  backupCapturing: boolean
  /** The note ID created at recording start */
  noteId: number | null
  /** The full note object — available immediately, no second fetch needed */
  noteData: Note | null
  /** Result from the generate API — consumed by NoteDetail */
  generateResult: GenerateResult | null
  /** Set by cancelProcessing so the in-flight fetch result is ignored */
  processingCancelled: boolean
  /** Current note textarea text — set by NoteDetail so sidebar stop can access it */
  currentNoteText: string
  /** The doctor's "Your notes" jottings during a recording (→ `doctor_notes`).
   *  These guide the AI note and are kept beside it; the AI always writes the
   *  body. This is the canonical capture field — never the note body
   *  (CLA-772 / the AI-authors model). */
  currentDoctorNotes: string
  /** Appointment context when recording was started from calendar */
  appointmentContext: AppointmentContext | null
  /**
   * Recordings whose audio is on disk but not yet committed (CLA-938). Newest
   * first. Empty is the normal state; a non-empty list is a standing "your
   * consult audio is safe but not submitted" affordance, and is the reason a
   * failed commit no longer silently resets to `idle`.
   */
  pendingUploads: PendingUpload[]
  /**
   * Set when the audio could NOT be persisted (no IndexedDB, or the origin is
   * out of quota). Told to the clinician verbatim, because it is the one case
   * where closing the tab really does lose the consult — the opposite of the
   * reassurance `pendingUploads` carries. Cleared on the next stop.
   */
  persistenceWarning: string | null
  /**
   * 30 minutes of continuous silence while recording (CLA-1564): the
   * "Still recording — no sound for 30 minutes." prompt is up. Cleared by
   * "Keep recording", by sound returning, or by the recording ending. Left
   * unanswered, the recording is finalised at 60 minutes of silence.
   */
  silenceWarning: boolean
  /**
   * The same clinician's OTHER live recordings that have been quiet 10+ min,
   * as named by the latest capture heartbeat (CLA-1564). Drives the passive
   * run-on banner. Empty unless a capture is live; replaced wholesale on each
   * heartbeat that carries a list, untouched by one that does not.
   */
  otherLiveRecordings: LiveRecordingSummary[]
  /** Note ids the clinician has dismissed the run-on banner for. */
  runOnDismissed: number[]

  /** Create a new note and start recording */
  /**
   * Start a recording into a new draft note. `patientUuid` creates the note
   * already linked to that patient (CLA-1730) — a link made afterwards could
   * be lost, leaving the note off the patient's Notes and Timeline.
   */
  startRecording: (opts?: { profileId?: number; patientUuid?: string }) => Promise<void>
  /** Start a consultation from an appointment — creates note with appointment link, then records */
  startConsultation: (ctx: AppointmentContext) => Promise<void>
  /** Start recording on an existing note (e.g. after cancelling a false start) */
  startRecordingForNote: (note: Note) => Promise<void>
  pauseRecording: () => void
  resumeRecording: () => void
  /**
   * Finish the recording and commit it. `reason` is set only when the stop was
   * NOT the clinician's — today, the 6-hour ceiling (CLA-969) — and is sent to
   * the server as `stopped_reason`. Anything else passed here (an `onClick`
   * event, say) is ignored and the stop is a normal one.
   */
  stopRecording: (reason?: StoppedReason) => Promise<void>
  /**
   * Ask, then throw the live recording away (v1.3 discard confirmation). The
   * server forgets the recording (`abandon_capture`, CLA-1880); the draft goes
   * too only when this recording created it and the clinician added nothing
   * to it. A stop that has already begun is left to commit. Resolves once the
   * clinician has answered; the server calls finish in the background.
   */
  cancelRecording: () => Promise<void>
  /**
   * Ask before throwing a live recording away (guideline v1.3): the app's
   * confirm with "Discard this recording?", the real length and patient, a
   * destructive Discard and "Keep recording" as the default. The recording
   * pauses while the dialog is open and resumes if it is kept or dismissed.
   * `withNote` says the draft note goes too; left out, it is true exactly
   * when `cancelRecording` would discard the draft (`discardTakesDraft`).
   * Resolves true to discard.
   */
  confirmDiscardRecording: (opts?: { withNote?: boolean; patientName?: string | null }) => Promise<boolean>
  /** Stop recording without confirmation, keeping the draft — used when the note is discarded */
  forceStopRecording: () => void
  /** Cancel during processing — sets flag so result is ignored */
  cancelProcessing: () => void
  /** Dismiss the terminal `error` banner. Recording start already clears it;
   *  this is so a doctor can put the message away without starting again. */
  dismissError: () => void
  /** Called by NoteDetail after consuming the result */
  clearGenerateResult: () => void
  /** Called by WebSocket handler when async generate completes */
  setGenerateResult: (result: GenerateResult) => void
  /** Clear noteId/noteData so NotesView falls through to cached data */
  clearNoteData: () => void
  /** Called by NoteDetail on every text change */
  setCurrentNoteText: (text: string) => void
  /** Called by the note editor with the title as typed, while `noteId` records. */
  setCurrentNoteTitle: (noteId: number, title: string) => void
  /** Set the "Your notes" jottings (the recording-screen notes box). */
  setCurrentDoctorNotes: (text: string) => void

  /**
   * Re-attempt the commit for a persisted recording (CLA-938). Always takes the
   * ATOMIC path with the recording's original Idempotency-Key — see the comment
   * on the implementation for why progressive can never be retried.
   */
  retryUpload: (pendingId: number) => Promise<void>
  /** Discard a persisted recording and its audio. Destructive; UI confirms. */
  discardPendingUpload: (pendingId: number) => Promise<void>
  /**
   * Load persisted recordings on app start so a tab crash, a closed laptop or a
   * dead API mid-upload surfaces as a recoverable entry rather than nothing at
   * all. Offers a retry — it does not fire one; automatic backoff retry is
   * Phase 2.
   */
  recoverPendingUploads: () => Promise<void>
  /** Dismiss the "could not save a local copy" warning. */
  clearPersistenceWarning: () => void
  /**
   * "Keep recording" on the silence prompt: restart the silence clock and put
   * the prompt away. Re-arms rather than disables — another 30 minutes of
   * silence warns again, and 60 still stops.
   */
  keepRecordingThroughSilence: () => void
  /**
   * Hide the run-on banner for the recordings it names right now. One that
   * drops off the list and later returns shows again.
   */
  dismissRunOnBanner: () => void
}

interface Capture {
  recorder: MediaRecorder
  stream: MediaStream
  /**
   * Chunks for THIS recording only. The recorder's `ondataavailable` closes
   * over this array, so a stale / discarded recorder that fires a late chunk
   * pushes into its own (dead) buffer — never into the next recording's. This
   * is the fix for F-1005: rapid record → discard → record in one tab used to
   * share a module-level buffer, corrupting subsequent recordings into silence.
   */
  chunks: Blob[]
  /** Live mic-level monitor — best-effort; absent in jsdom / unsupported browsers. */
  audioContext?: AudioContext
  analyser?: AnalyserNode
  /**
   * Progressive upload (CLA-465 hardening). `noteId` is the draft this capture
   * streams into; `chunkIndex` is the next ordered index to assign; `uploadChain`
   * serialises the per-chunk appends so they land in order; `progressive` flips
   * false once the stream can no longer be trusted, at which point stop falls
   * back to the atomic full-blob upload.
   *
   * CLA-945 Phase 3: that flip is no longer "any error". A TRANSPORT failure is
   * retried and the stream kept alive, because during an API degradation
   * failing appends ARE the symptom — abandoning the server-side copy at the
   * first blip made the outage case the one that lost the partial. An INTEGRITY
   * failure (a reported gap, a 4xx) still falls back immediately.
   * `consecutiveFailures` is the circuit-breaker between a blip and an outage.
   */
  noteId: number
  chunkIndex: number
  uploadChain: Promise<void>
  progressive: boolean
  consecutiveFailures: number
  /**
   * Set the moment stop begins. Chunk retries are useful DURING a recording —
   * they keep the server-side copy complete through a blip — but they must
   * never be waited on at stop: the stop path awaits `uploadChain`, so a
   * retrying chunk would add seconds to the exact moment the clinician is
   * trying to move on. At stop the answer is always "give up on progressive
   * and upload the blob", which is fast and already proven.
   */
  stopping: boolean
  /**
   * Set synchronously on the first `stopRecording` for this capture, before
   * any await. A second stop — the ceiling firing as the clinician presses
   * Stop, a double-tap — returns immediately instead of running a second
   * commit of the same audio (CLA-969). `capture` itself is not cleared until
   * the recorder has finalized, so it cannot serve as this guard.
   */
  stopBegun: boolean
  /**
   * The draft this capture streams into was created for it, by `POST /notes`
   * in `startRecording` / `startConsultation` (CLA-1875). False when recording
   * into a note that already existed (`startRecordingForNote`): Cancel must
   * never discard that note, which may hold the clinician's typed notes or a
   * generated body; Cancel takes only the recording off it (CLA-1880).
   */
  draftMadeForCapture: boolean
  /**
   * The note's title when the capture began, and the title the clinician has
   * typed since (null until the note's editor reports one; see
   * `setCurrentNoteTitle`). A recording-made draft whose title was typed is
   * kept when the recording is discarded (CLA-1880).
   */
  createdTitle: string
  title: string | null
  /**
   * The silence auto-stop clock for this capture (CLA-1564). Fed from the
   * same analyser as the live level monitor; see `startSilenceLoop`.
   */
  silence: SilenceClock
  /**
   * `recording_stopped` has been sent for this capture. Every capture that
   * sent `recording_started` sends exactly one — on Stop, on an automatic
   * stop, or on discard — so started minus stopped counts only captures that
   * never ended (a closed or crashed tab).
   */
  stopReported: boolean
  /**
   * Tells the server what state this capture is in (CLA-1342) so the stalled-
   * recording detector can tell a pause from a dead mic. Advisory; nothing
   * about the recording waits on it.
   */
  heartbeat: CaptureHeartbeat
  /**
   * Reports WHY the mic died (`mic_lost` / `mic_recovered`, CLA-1606), one
   * event per `captureLost` episode. Telemetry only.
   */
  micLoss: MicLossReporter
  /**
   * Listeners and locks that live exactly as long as this capture (CLA-1341):
   * the tab-visibility check, the screen wake lock. Run at every exit —
   * stop, discard, takeover — so nothing from a dead capture reaches the
   * next one.
   */
  cleanup: Array<() => void>
}

let capture: Capture | null = null
/**
 * One recording start at a time (CLA-1459, audit finding 9).
 *
 * Every start path guards on `state`, and that guard cannot do this job: the
 * store only becomes `recording` inside `beginCapture`, which is TWO awaits
 * later — `getUserMedia`, then `POST /notes`. Two Record presses therefore
 * both read `idle`, both pass, and both go on to build a `MediaRecorder` and
 * create a note.
 *
 * The damage is in the second `beginCapture`: it overwrites [capture] and the
 * segment roller, so the FIRST recorder is orphaned. It keeps streaming into a
 * different note, no Stop owner controls it, and if its progressive upload
 * fails its in-memory audio has no stop or persist path at all — the UI is
 * driving the second recording the whole time.
 *
 * Reproduced by the audit against the real store: two concurrent starts
 * produced 2 recorders where 1 was expected.
 *
 * The claim is taken SYNCHRONOUSLY, before the first await. That is the only
 * placement that closes the window — anything after an await is the bug.
 */
let startToken = 0
let startHeld = false

/** Reserve the right to start, or null if a start is already in flight. */
function claimStart(): number | null {
  if (startHeld) return null
  startHeld = true
  return ++startToken
}

/**
 * Still the live start? False once something else has taken the store, which
 * is the cue to dispose of whatever this start acquired rather than install
 * it over the top.
 */
function startIsCurrent(token: number): boolean {
  return startHeld && token === startToken
}

function releaseStart(token: number): void {
  if (token === startToken) startHeld = false
}
/**
 * Client-side segmentation (ADR-020 Brick 2, web port). Lifecycle mirrors
 * `capture` exactly; segments are transcription accelerators only — see
 * lib/segmentRoller.ts for the safety model.
 */
let segmentRoller: SegmentRoller | null = null
let timerInterval: ReturnType<typeof setInterval> | null = null
/**
 * How long a well-behaved recorder gets to deliver `stop` before we treat its
 * finalization as LATE. Reaching this is no longer a reason to read the buffer
 * and move on (CLA-1432) — it is the point at which we say so.
 */
const STOP_SETTLE_MS = 5000
/**
 * Quiet period after a post-stop `dataavailable` before the recording is
 * declared finalized (CLA-1432). A recorder that flushes its tail without ever
 * firing `stop` is finished the moment it goes quiet; waiting out the whole
 * deadline for it would add pointless seconds. Reset by each further chunk, so
 * a tail delivered in two pieces is fully collected.
 */
const TAIL_QUIET_MS = 500
/**
 * Hard bound on finalization (CLA-1432).
 *
 * The old code waited `STOP_SETTLE_MS` and then **discarded** whatever came
 * after: it detached `ondataavailable`, froze the Blob, and persisted and
 * uploaded that prefix. A final chunk arriving a second later had nowhere to
 * go, and the integrity hash still passed — because it describes what the
 * client assembled, not whether what the client assembled is the whole
 * recording. So a shortened consult was committed as complete with nothing
 * anywhere reporting otherwise.
 *
 * This bound is NOT the clinician's wait. The store enters `processing` before
 * the stop path awaits anything, so they are already out of the recording UI;
 * what this delays is only the commit, and only for a recorder that has gone
 * silent mid-finalization. It is well inside `PROCESSING_WATCHDOG_MS`, and it
 * ends early the moment the tail lands (`TAIL_QUIET_MS`) — a healthy recorder
 * never feels it.
 *
 * Reaching it means the recorder never answered (CLA-1341: an OS-suspended
 * track may never deliver `stop`). We still finish with what was captured —
 * that part was always right — but the result is reported as unconfirmed
 * rather than certified complete.
 */
const FINALIZE_DEADLINE_MS = 20_000
/**
 * Wall-clock duration accounting (CLA-1267).
 *
 * `elapsed` used to be a COUNTER — a `setInterval` that did `elapsed + 1`. That
 * measures how many times a timer fired, not how long the consult ran, and
 * browsers throttle timers hard in hidden/backgrounded tabs while MediaRecorder
 * keeps capturing at full rate. The error was therefore structural and always
 * in the under-reporting direction. Measured on a real prod consult: the UI
 * showed 37:22 for a recording whose bytes prove it held at least 60 minutes of
 * audio — it had lost 23+ minutes, which is also why that upload's size was
 * mis-predicted and its failure went undiagnosed for a day.
 *
 * Now: accumulate completed recording spans, and add the live span on read. The
 * interval still fires ~1/s but only to REPAINT — throttling it costs UI
 * smoothness, never the value.
 *
 * `Date.now()` rather than `performance.now()` on purpose: this is meant to be
 * wall-clock consult length, and `performance.now()` stalls across system sleep
 * on some platforms. Clamped so a backwards clock adjustment cannot rewind it.
 */
let recordedMs = 0
let spanStartedAt: number | null = null

/** Seconds of recording so far — completed spans plus the one in flight. */
function elapsedSeconds(): number {
  const live = spanStartedAt === null ? 0 : Math.max(0, Date.now() - spanStartedAt)
  return Math.floor((recordedMs + live) / 1000)
}

/** Begin (or resume) accounting a recording span. */
function openDurationSpan(reset: boolean) {
  if (reset) recordedMs = 0
  spanStartedAt = Date.now()
}

/** Close the current span into the accumulated total. Idempotent. */
function closeDurationSpan() {
  if (spanStartedAt === null) return
  recordedMs += Math.max(0, Date.now() - spanStartedAt)
  spanStartedAt = null
}
/** Live mic-level poll handle + accumulated near-silent time (ms). */
let levelInterval: ReturnType<typeof setInterval> | null = null
let silentMs = 0

// Live silence detection. A muted mic — or one held by another app (e.g. the
// doctor is running Heidi in parallel) — yields a flat, near-zero signal for the
// whole session. We surface a warning after a few seconds of continuous silence
// so it's caught immediately, not via an empty note minutes later. The warning
// self-clears the instant real audio returns, so a natural pause doesn't strand it.
const LEVEL_POLL_MS = 250
const SILENCE_RMS_THRESHOLD = 0.0025
const SILENCE_WARN_AFTER_MS = 5000
/** Debounce handle for live-saving the "Your notes" jottings during recording. */
let doctorNotesSaveTimer: ReturnType<typeof setTimeout> | null = null

/**
 * Processing watchdog (CLA-1359).
 *
 * `stopRecording` enters `state: 'processing'`, and every path back to `idle`
 * — sync response, async response, and the catch — sits *after* the commit
 * `await`. Those paths are correct. All of them are unreachable if the commit
 * promise never settles, and `@/api/client` carries no `AbortController` or
 * timeout, so a socket that stalls after the request is sent neither resolves
 * nor rejects. The store is then pinned on `processing` until a page refresh
 * rebuilds it, which is exactly why refreshing "fixes" it.
 *
 * Dr Brandon hit this on 2026-09-13: note 21856 was fully transcribed and
 * written server-side (a 60-minute consult) while his browser sat on
 * "Submitting your recording", and refreshing was the only way to the next
 * patient.
 *
 * The fix is deliberately NOT a timeout on the request. The atomic fallback
 * path posts the whole blob, and an hour-long consult is tens of megabytes
 * that can legitimately take minutes — a blanket timeout would convert slow
 * uploads into failures, which is worse than the bug. Instead: after a
 * generous wait, ASK THE SERVER what happened to the note and release the UI
 * on the answer.
 *
 * iOS (CLA-346) and Android (CLA-571) already shipped their own versions of
 * this; web never did.
 */
const PROCESSING_WATCHDOG_MS = 90_000
let processingWatchdog: ReturnType<typeof setTimeout> | null = null
/**
 * Bumped on every stop. The watchdog captures the value at arm time and bails
 * if it no longer matches, so it is SELF-CANCELLING: the dozen-odd paths that
 * already reset to `idle` need no teardown call, and a stale timer from a
 * previous consult can never act on a newer one.
 */
let processingEpoch = 0

function clearProcessingWatchdog() {
  if (processingWatchdog) {
    clearTimeout(processingWatchdog)
    processingWatchdog = null
  }
}

/**
 * Ask the server what became of `noteId`, then release the recorder.
 *
 * ⚠ This NEVER deletes the pending IndexedDB entry (CLA-938). Whether the
 * commit landed is unknown from here even when the note looks finished — the
 * note may predate this recording — and the original Idempotency-Key means a
 * retry against an upload that did land dedups rather than duplicates. Audio
 * kept unnecessarily is recoverable; audio dropped is not. Deleting the entry
 * stays the exclusive business of the commit path's confirmed-2xx branch.
 */
async function reconcileStuckProcessing(
  epoch: number,
  noteId: number | null,
  get: () => RecordingStore,
  set: (partial: Partial<RecordingStore>) => void,
): Promise<void> {
  // Self-cancelling on all three axes: a newer stop, a session that already
  // left processing normally, and a newer consult owning the store (F-1).
  // A null noteId is NOT a takeover — the WS `note_changed` handler clears it
  // while the commit is still in flight (see `stillOwns` in stopRecording).
  if (epoch !== processingEpoch) return
  if (get().state !== 'processing') return
  if (get().noteId !== null && get().noteId !== noteId) return

  if (noteId == null) {
    set({
      state: 'idle',
      elapsed: 0,
      error: 'Still submitting — you can carry on; this recording will finish in the background.',
    })
    return
  }

  try {
    const note = await api.get<Note>(`/notes/${noteId}`)
    // Re-check after the await: the real commit may have landed meanwhile.
    if (epoch !== processingEpoch || get().state !== 'processing') return

    const stage = (note.processing_stage as string | null) ?? null
    const hasBody = (note.note ?? '').trim() !== ''

    if (stage === null && hasBody) {
      // It finished while the UI was stuck — the common case, and Brandon's.
      // Hand the note over and let the clinician move on.
      set({
        state: 'idle',
        elapsed: 0,
        noteId: null,
        noteData: note,
        currentNoteText: '',
        currentDoctorNotes: '',
        appointmentContext: null,
      })
    } else {
      // It arrived and is still transcribing or generating. Free the recorder
      // but keep the note selected so its own processing state is what shows.
      set({
        state: 'idle',
        elapsed: 0,
        noteData: note,
        currentNoteText: '',
        currentDoctorNotes: '',
      })
    }
  } catch {
    // Could not ask — offline, or the note is not there. Release the recorder
    // so the next patient is not blocked, say so honestly, and let the CLA-938
    // queue own the retry.
    if (epoch !== processingEpoch || get().state !== 'processing') return
    set({
      state: 'idle',
      elapsed: 0,
      error: 'Still submitting — this recording is saved and will retry. You can start the next one.',
    })
    void get().recoverPendingUploads()
  }
}

/**
 * Pending-upload ids with a retry in flight (CLA-938). The single source of
 * truth for `PendingUpload.retrying`, so every re-read of the queue derives the
 * flag rather than copying the previous projection's value forward.
 */
const retryingIds = new Set<number>()

function clearDoctorNotesSave() {
  if (doctorNotesSaveTimer) {
    clearTimeout(doctorNotesSaveTimer)
    doctorNotesSaveTimer = null
  }
}

/**
 * Fully tear down a capture: detach handlers so no late events can fire, stop
 * the recorder if still active, and release the mic tracks. Idempotent.
 */
function teardownCapture(c: Capture | null) {
  if (!c) return
  teardownLevelMonitor(c)
  c.heartbeat.stop()
  runCleanup(c)
  c.recorder.ondataavailable = null
  c.recorder.onstop = null
  c.recorder.onerror = null
  if (c.recorder.state !== 'inactive') {
    try {
      c.recorder.stop()
    } catch {
      /* already stopping */
    }
  }
  c.stream.getTracks().forEach((t) => t.stop())
}

/**
 * The server explicitly refused these bytes (CLA-1430).
 *
 * A note already `processing` a DIFFERENT recording now answers 409
 * `audio_not_accepted` instead of returning a receipt that reads as
 * acceptance. That is the fix working, not a failure: the audio is safe on
 * this device and a retry will succeed once the note finishes.
 *
 * It needs its own message for the reason the Android worker spells out at
 * length (CLA-963): telling a clinician their consult failed when it is safe
 * and queued is the worst available outcome — it invites them to re-record a
 * consultation that was never at risk. "Upload failed (409)" would do exactly
 * that.
 */
function isAudioNotAccepted(e: unknown): boolean {
  if (!(e instanceof ApiError) || e.status !== 409) return false
  const body = e.body
  return typeof body === 'object' && body !== null && (body as { status?: unknown }).status === 'audio_not_accepted'
}

/**
 * How a recording's finalization ended.
 *
 * `stopped` — the recorder answered: it fired `stop`, or it flushed its tail
 *   and went quiet. Everything it captured is in the buffer.
 * `unconfirmed` — it never answered within `FINALIZE_DEADLINE_MS`. We hold what
 *   it gave us, and we do not know whether that is all of it.
 */
type Finalization = 'stopped' | 'unconfirmed'

/**
 * Stop the recorder and wait for it to finish, WITHOUT ever throwing away what
 * it delivers late (CLA-1432).
 *
 * The contract this exists to keep: a deadline may stop us *waiting*; it must
 * never discard final audio, and it must never let an unfinished recording be
 * reported as a complete one. `ondataavailable` is therefore left attached
 * throughout — the buffer keeps an owner for the whole of finalization — and
 * the caller learns which way it ended.
 *
 * Ends as early as it honestly can: on `stop`, or on a tail that has gone quiet
 * for `TAIL_QUIET_MS`. Only a recorder that says nothing at all pays the full
 * deadline, and that is the case (CLA-1341) where there is nothing to wait for.
 */
function settleRecorderStop(c: Capture): Promise<Finalization> {
  return new Promise<Finalization>((resolve) => {
    // Already inactive: the recorder died with its track, so `stop` will never
    // fire and nothing further is coming. Not a timeout — a finished recorder.
    if (c.recorder.state === 'inactive') {
      resolve('stopped')
      return
    }

    let settled = false
    let quiet: ReturnType<typeof setTimeout> | null = null

    const finish = (outcome: Finalization) => {
      if (settled) return
      settled = true
      if (quiet) clearTimeout(quiet)
      clearTimeout(deadline)
      resolve(outcome)
    }

    const deadline = setTimeout(() => finish('unconfirmed'), FINALIZE_DEADLINE_MS)

    c.recorder.onstop = () => finish('stopped')

    // Chain rather than replace: the live handler is what buffers the chunk and
    // keeps the progressive stream going. This only observes that a chunk
    // arrived after Stop, which is the signal the recorder is flushing.
    const buffer = c.recorder.ondataavailable
    c.recorder.ondataavailable = (e) => {
      buffer?.call(c.recorder, e)
      if (settled) return
      if (quiet) clearTimeout(quiet)
      quiet = setTimeout(() => finish('stopped'), TAIL_QUIET_MS)
    }

    try {
      c.recorder.stop()
    } catch {
      // `stop()` on a recorder the browser has already torn down. It will not
      // answer, and nothing is in flight to wait for.
      finish('unconfirmed')
    }
  })
}

/**
 * One `recording_stopped` event per stop, carrying the silence measurements
 * the web floor has to be calibrated from (CLA-1564).
 *
 * Web levels are post-AGC and sit ~23 dB above iOS, and no web recording left
 * running has ever been measured, so `SILENCE_FLOOR_DBFS` is a conservative
 * estimate rather than a measurement. This is how it becomes one: the longest
 * continuous silent stretch at the active floor (`max_silence_ms`) and at a
 * ladder of other floors, plus the 10th/50th percentile window level — what
 * this analyser actually reads in a quiet room versus in conversation. Levels
 * and durations only: no audio, nothing identifying.
 *
 * `recording_stopped` is already on the server allowlist (shared with iOS);
 * `platform: 'web'` separates the two.
 */
function reportRecordingStopped(
  c: Capture,
  noteId: number | null,
  elapsed: number,
  /** `null` = the clinician's Stop; `discarded` = the recording was thrown away. */
  stoppedReason: StoppedReason | 'discarded' | null,
): void {
  if (c.stopReported) return
  c.stopReported = true
  const stats = c.silence.stats()
  const metadata: Record<string, string | number | boolean> = {
    platform: 'web',
    elapsed_seconds: elapsed,
    stopped_reason: stoppedReason ?? 'user',
    silence_floor_dbfs: SILENCE_FLOOR_DBFS,
    max_silence_ms: stats.maxSilenceMs,
    level_windows: stats.windows,
    unmeasured_windows: stats.unmeasuredWindows,
  }
  for (const [floor, ms] of Object.entries(stats.maxSilenceMsByFloor)) {
    // `max_silence_ms_m45` = longest stretch below −45 dBFS.
    metadata[`max_silence_ms_m${Math.abs(Number(floor))}`] = ms
  }
  if (stats.levelP10Dbfs !== null) metadata.level_p10_dbfs = stats.levelP10Dbfs
  if (stats.levelP50Dbfs !== null) metadata.level_p50_dbfs = stats.levelP50Dbfs
  trackClientEvent('recording_stopped', metadata, { note_id: noteId })
}

/**
 * A chunk that arrived after the blob was read (CLA-1432).
 *
 * It cannot be spliced into the committed recording from here: a re-commit
 * under the same Idempotency-Key can come back as the existing `processing`
 * note WITHOUT the new bytes being attached, which is CLA-1430 — the very
 * failure where a client destroys its last copy on a receipt that never
 * accepted it. So this does the one thing that is both honest and safe: it
 * makes the loss visible, loudly and with its size, instead of letting a
 * detached handler swallow it the way the old code did.
 *
 * Whether this ever fires in the field is the measurement that decides how
 * much more this deserves. Today we have no evidence either way, because
 * nothing has ever looked.
 */
function reportLateTail(c: Capture, e: { data: Blob }, noteId: number | null): void {
  if (e.data.size === 0) return
  console.warn(
    `[recording] ${e.data.size} bytes arrived after finalization for note ${noteId ?? 'unknown'} — not in the committed recording`,
  )
  trackClientEvent('recording_tail_after_commit', {
    note_id: noteId ?? 0,
    bytes: e.data.size,
    chunks_before: c.chunks.length,
  })
}

/**
 * Decode the recorded audio and report whether it's effectively silent (peak
 * amplitude below an audible floor). Catches a muted / dead mic track that
 * produced a full-length but silent recording, so we can prompt an immediate
 * re-record instead of uploading silence and surfacing a red error ~90s later.
 * Fails open (returns false) if decoding isn't possible, so a real recording is
 * never wrongly rejected.
 */
async function isEffectivelySilent(blob: Blob): Promise<boolean> {
  try {
    const Ctx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!Ctx) return false

    const ctx = new Ctx()
    const audio = await ctx.decodeAudioData(await blob.arrayBuffer())
    let peak = 0
    for (let ch = 0; ch < audio.numberOfChannels; ch++) {
      const data = audio.getChannelData(ch)
      const step = Math.max(1, Math.floor(data.length / 100_000))
      for (let i = 0; i < data.length; i += step) {
        const amp = Math.abs(data[i])
        if (amp > peak) peak = amp
      }
    }
    await ctx.close()
    // Speech peaks ~0.1–1.0; ~0.005 (≈ −46 dBFS) is effectively silence.
    return peak < 0.005
  } catch {
    return false
  }
}

/**
 * Attach a Web Audio analyser to the live mic stream so we can measure input
 * level during recording. Best-effort — silently no-ops where Web Audio is
 * unavailable (jsdom, old browsers), so it can never block a recording.
 */
function setupLevelMonitor(c: Capture, stream: MediaStream) {
  try {
    const Ctx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!Ctx) return
    const audioContext = new Ctx()
    const source = audioContext.createMediaStreamSource(stream)
    const analyser = audioContext.createAnalyser()
    // ~100 ms windows for the silence auto-stop clock (CLA-1564). The live
    // "no audio" nudge reads the same node and sizes its buffer from this.
    analyser.fftSize = SILENCE_WINDOW_SAMPLES
    source.connect(analyser)
    c.audioContext = audioContext
    c.analyser = analyser
  } catch {
    /* monitoring is best-effort; never block recording */
  }
}

/**
 * Poll the analyser and flip `audioWarning` after SILENCE_WARN_AFTER_MS of
 * continuous near-silence, clearing it the moment audio returns. Runs only while
 * state is 'recording'. Idempotent (no-ops if already running or no analyser).
 */
function startLevelLoop(set: (s: Partial<RecordingStore>) => void) {
  const analyser = capture?.analyser
  if (!analyser || levelInterval) return
  const data = new Uint8Array(analyser.fftSize)
  silentMs = 0
  levelInterval = setInterval(() => {
    const a = capture?.analyser
    if (!a) return
    if (useRecordingStore.getState().state !== 'recording') return

    a.getByteTimeDomainData(data)
    let sumSq = 0
    for (let i = 0; i < data.length; i++) {
      const v = (data[i] - 128) / 128
      sumSq += v * v
    }
    const rms = Math.sqrt(sumSq / data.length)

    if (rms < SILENCE_RMS_THRESHOLD) {
      silentMs += LEVEL_POLL_MS
      if (silentMs >= SILENCE_WARN_AFTER_MS && !useRecordingStore.getState().audioWarning) {
        set({ audioWarning: true })
      }
    } else {
      silentMs = 0
      if (useRecordingStore.getState().audioWarning) set({ audioWarning: false })
    }
  }, LEVEL_POLL_MS)
}

/** Stop the level loop and release the AudioContext. Idempotent. */
function teardownLevelMonitor(c: Capture | null) {
  if (levelInterval) {
    clearInterval(levelInterval)
    levelInterval = null
  }
  silentMs = 0
  stopSilenceLoop(c)
  const ctx = c?.audioContext
  if (ctx) {
    try {
      ctx.close()
    } catch {
      /* already closed */
    }
    c!.audioContext = undefined
    c!.analyser = undefined
  }
}

/**
 * Progressive-upload chunk cadence. 5s balances request volume over a long
 * consult (a 45-min recording ≈ 540 appends) against getting audio onto the
 * server promptly; a 1-min test still yields ~12 chunks, exercising the full
 * ordered-append + reconcile path.
 */
const CHUNK_MS = 5000

/**
 * Recorder output format, in preference order (CLA-1266).
 *
 * This used to be `isTypeSupported('audio/webm;codecs=opus') ? that :
 * 'audio/webm'` — a check that only guarded the CODEC suffix and then fell back
 * to a container the same browser may equally not support, in which case the
 * `MediaRecorder` constructor throws. Safari has historically shipped
 * MediaRecorder with MP4/AAC and no WebM at all.
 *
 * Every entry here is on the server's ingest allowlist
 * (`Claro.AI.AudioValidation`), so whichever one a browser picks is accepted
 * and labelled correctly downstream. If none match we pass no `mimeType` and
 * let the browser choose its own default — a container we did not ask for is
 * still far better than refusing to record.
 */
const MIME_TYPE_PREFERENCE = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4;codecs=mp4a.40.2',
  'audio/mp4',
]

/**
 * Explicit encoder bitrate (CLA-1266).
 *
 * Nothing set this before, so bytes-per-second was whatever each browser
 * defaulted to — which made upload size an unspecified input that varied by
 * browser and version, and made any size-ceiling reasoning guesswork. Measured
 * prod default on Safari/macOS was ~220 kbps: roughly 100 MB per hour, which is
 * how a long consult reached Cloudflare's 100 MB body cap.
 *
 * 64 kbps matches the iOS recorder and is ample for speech — ~28 MB/hour, so an
 * hour-long consult is comfortably deliverable and the size model becomes
 * arithmetic rather than empirical.
 */
const AUDIO_BITS_PER_SECOND = 64_000

/** SHA-256 of a blob as lowercase hex — the client side of the server's
 *  integrity gate. */
async function sha256Hex(blob: Blob): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer())
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

/**
 * Append one chunk to the note's server-side audio file during recording.
 * Ordered + idempotent server-side (audio_append keyed on chunk_index). A `gap`
 * response — or any error — flips the capture out of progressive mode so stop
 * falls back to the atomic full-blob upload. Never throws: a failed append must
 * only cost the optimisation, never the recording.
 */
async function appendChunk(c: Capture, idx: number, data: Blob): Promise<void> {
  if (!c.progressive) return

  for (let attempt = 0; attempt <= APPEND_RETRIES; attempt++) {
    try {
      const form = new FormData()
      form.append('chunk_index', String(idx))
      form.append('audio', data, `chunk-${idx}.webm`)
      const res = await api.postForm<{ status?: string }>(`/notes/${c.noteId}/audio_append`, form)
      if (res?.status === 'gap') {
        // The server's own judgement that the stream is broken. Retrying
        // cannot repair a gap, so stop immediately.
        c.progressive = false
        return
      }
      c.consecutiveFailures = 0
      return
    } catch (e) {
      if (classifyAppendFailure(e) === 'integrity') {
        c.progressive = false
        return
      }
      // Transport. Retry the SAME index — appends are idempotent server-side on
      // chunk_index, so re-sending one that actually landed is harmless.
      if (attempt < APPEND_RETRIES && !c.stopping) {
        await new Promise((r) => setTimeout(r, APPEND_RETRY_DELAY_MS))
        continue
      }
      if (c.stopping) {
        // Never make the clinician wait on a retry. Fall back to atomic.
        c.progressive = false
        return
      }
      // Retries exhausted for this chunk. One lost chunk does not condemn the
      // stream — the whole-file hash gate at commit catches any real gap and
      // falls back — but a RUN of them means the API is down rather than
      // blipping, and continuing would queue chunks (and their blobs) behind
      // retries that will not succeed. That load-shedding is what the old
      // fail-fast behaviour got right, and it is preserved here.
      c.consecutiveFailures += 1
      if (c.consecutiveFailures >= MAX_CONSECUTIVE_CHUNK_FAILURES) {
        c.progressive = false
      }
      return
    }
  }
}

/**
 * The `/notes/generate` form shared by the live stop path and by every retry of
 * a persisted recording (CLA-938). Canonical capture (CLA-772): the doctor's
 * jottings go out as "Your notes" (→ doctor_notes), never as the body. The audio
 * field — `audio` (atomic) or `audio_sha256`+`audio_size` (progressive) — is
 * added by the caller.
 */
function buildGenerateForm(
  noteId: number | null,
  userNotes: string,
  stoppedReason: StoppedReason | null,
): FormData {
  const form = new FormData()
  form.append('treatments', '')
  form.append('clinical_type', 'auto')
  form.append('source', 'web')
  if (noteId) form.append('existing_note_id', String(noteId))
  if (userNotes !== '') {
    form.append('user_notes', userNotes)
    form.append('doctor_notes_guide', 'true')
  }
  // CLA-969. Only for a stop that was not the clinician's; a normal Stop sends
  // nothing. Older servers ignore an unknown field, so this is safe to send
  // ahead of the server release.
  const reason = toStoppedReason(stoppedReason)
  if (reason) form.append('stopped_reason', reason)
  return form
}

/**
 * Commit the full blob atomically. The only path a retry can take: the
 * progressive commit sends `audio_sha256`+`audio_size` and depends on
 * `capture.progressive` plus the server's assembled chunks, and `capture` is
 * nulled the moment recording stops — a `Capture` cannot survive a reload, let
 * alone a crash. Reusing the recording's original Idempotency-Key is what makes
 * this safe if a progressive commit partially succeeded: the server dedups the
 * atomic retry onto the same note row rather than writing a second one.
 */
function commitAtomicUpload(
  blob: Blob,
  noteId: number | null,
  userNotes: string,
  idempotencyKey: string,
  stoppedReason: StoppedReason | null,
): Promise<Note> {
  const form = buildGenerateForm(noteId, userNotes, stoppedReason)
  form.append('audio', blob, 'recording.webm')
  return api.postForm<Note>('/notes/generate', form, {
    headers: { 'Idempotency-Key': idempotencyKey },
  })
}

/**
 * Slice size for a chunked commit (CLA-1243).
 *
 * Small enough to sit far below any edge body cap — Cloudflare's is 100 MB on
 * this plan — and large enough that a 40-minute consult is ~17 requests rather
 * than the ~500 a 5s recording cadence would produce.
 */
const UPLOAD_SLICE_BYTES = 4 * 1024 * 1024

/**
 * The persisted audio can no longer be read back out of browser storage.
 *
 * Distinct from every other upload failure because the remedy is different:
 * nothing the server does, and no number of retries, brings the bytes back.
 */
export class RecordingUnreadableError extends Error {
  constructor() {
    super('This recording can no longer be read from your browser')
    this.name = 'RecordingUnreadableError'
  }
}

/**
 * Prove the persisted audio is still readable BEFORE building an upload around
 * it (CLA-1243).
 *
 * A Blob handed back by IndexedDB is a promise about bytes, not the bytes — the
 * read can fail later, and when it does mid-`fetch` the body stream dies, the
 * request is aborted with nothing sent, and the clinician gets
 * "Upload failed (unknown)" plus a Retry button that will never work. That is
 * the exact opaque dead end CLA-1243 set out to remove, arriving by a second
 * route.
 *
 * Reading 1 KB is enough: the failure is the handle, not the length.
 *
 * ⚠ This is a PROBE, not a proof — the read can still fail later on a
 * different part of the blob. It exists so the common case is legible and so
 * an unreadable recording is DISTINGUISHABLE in telemetry from a rejected one,
 * which is the thing we could not tell apart on 2026-09-04.
 */
async function assertReadable(blob: Blob): Promise<void> {
  try {
    await blob.slice(0, Math.min(blob.size, 1024)).arrayBuffer()
  } catch {
    throw new RecordingUnreadableError()
  }
}

/**
 * Commit a persisted recording as a CHUNK SEQUENCE rather than one request.
 *
 * The atomic path sends the whole consult in a single POST, and that request is
 * not always deliverable. Cloudflare caps a request body at 100 MB here and
 * answers 413 while the browser is still streaming, so `fetch` rejects with a
 * bare network error the client cannot even read a status from — the clinician
 * sees "Upload failed (unknown)" and every retry re-sends the same oversized
 * blob. On 2026-09-04 that made a real 37-minute consult undeliverable: 14
 * attempts, not one of which reached the app.
 *
 * Slicing removes the ceiling entirely. `reset=1` on slice 0 truncates whatever
 * partial the abandoned stream left behind and rewinds the server's chunk
 * counter, so this is a genuine re-upload rather than an append onto stale
 * bytes — then the commit goes through the SAME `audio_sha256` + `audio_size`
 * gate a live progressive stream uses, so the server still proves it assembled
 * exactly the bytes the client holds.
 *
 * Sequential on purpose: `audio_append` is ordered, and a parallel slice that
 * arrived early would read as a gap.
 */
async function commitChunkedUpload(
  blob: Blob,
  noteId: number,
  userNotes: string,
  idempotencyKey: string,
  stoppedReason: StoppedReason | null,
): Promise<Note> {
  const slices = Math.ceil(blob.size / UPLOAD_SLICE_BYTES)

  for (let i = 0; i < slices; i++) {
    const form = new FormData()
    form.append('chunk_index', String(i))
    // Only the first slice resets — the rest are ordinary ordered appends.
    if (i === 0) form.append('reset', '1')
    form.append(
      'audio',
      blob.slice(i * UPLOAD_SLICE_BYTES, (i + 1) * UPLOAD_SLICE_BYTES),
      `chunk-${i}.webm`,
    )
    const res = await api.postForm<{ status?: string }>(`/notes/${noteId}/audio_append`, form)
    // A gap here means the server and this client disagree about the sequence.
    // Retrying the whole upload is the repair; appending over it is not.
    if (res?.status === 'gap') {
      throw new Error(`Upload lost its place at part ${i + 1} of ${slices}`)
    }
  }

  const form = buildGenerateForm(noteId, userNotes, stoppedReason)
  form.append('audio_sha256', await sha256Hex(blob))
  form.append('audio_size', String(blob.size))
  const result = await api.postForm<Note | { status: 'reupload_required' }>(
    '/notes/generate',
    form,
    { headers: { 'Idempotency-Key': idempotencyKey } },
  )

  // Deliberately NOT falling back to the atomic path here. We just sent exactly
  // these bytes and the server says its assembly doesn't match them; the atomic
  // path is the one that cannot carry a recording this size, so falling back
  // would trade a real error for the silent dead end this function exists to
  // remove. Failing keeps the audio and the retry button.
  if (result.status === 'reupload_required') {
    throw new Error('Server could not verify the uploaded audio')
  }
  return result
}

/**
 * Commit a recording we hold in full — chunked when it is big enough that one
 * request is a risk, atomic otherwise.
 *
 * The threshold is the slice size: below it, chunking would be one request
 * either way, so the proven single-shot path stays. Above it, every consult
 * takes the chunked path — not just the ones near the cap — because a rarely
 * exercised recovery path is one that rots before it is needed.
 *
 * A recording with no `noteId` has nothing to append to and must go atomic.
 */
async function commitFullUpload(
  blob: Blob,
  noteId: number | null,
  userNotes: string,
  idempotencyKey: string,
  stoppedReason: StoppedReason | null,
): Promise<Note> {
  await assertReadable(blob)
  if (noteId != null && blob.size > UPLOAD_SLICE_BYTES) {
    return commitChunkedUpload(blob, noteId, userNotes, idempotencyKey, stoppedReason)
  }
  return commitAtomicUpload(blob, noteId, userNotes, idempotencyKey, stoppedReason)
}

/**
 * Turn a getUserMedia rejection into something a clinician can act on.
 *
 * The browser's own strings are for developers — Chrome says "Permission
 * denied", Safari says "The request is not allowed by the user agent". Shown
 * verbatim in the middle of a clinic they say nothing about what to do next,
 * and the three causes have three different remedies.
 */
function micErrorMessage(e: unknown): string {
  const name = (e as { name?: string })?.name
  switch (name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return 'Claro can’t use your microphone — allow microphone access for this site in your browser, then start again.'
    case 'NotFoundError':
    case 'DevicesNotFoundError':
      return 'No microphone found — connect one or pick an input in your system sound settings, then start again.'
    case 'NotReadableError':
    case 'TrackStartError':
      return 'Your microphone is in use by another app — close it (a video call, another Claro tab), then start again.'
    case 'OverconstrainedError':
      return 'That microphone can’t be used for recording — pick a different input in your system sound settings.'
    default:
      return e instanceof Error && e.message
        ? `Couldn’t start the microphone — ${e.message}`
        : 'Couldn’t start the microphone.'
  }
}

/**
 * Acquire a live microphone stream, or throw.
 *
 * Split out of `startAudioCapture` so callers can prove the microphone works
 * BEFORE they create a note. The old order — create the note, then ask for the
 * mic — left a note behind on every mic failure: the catch reset the client to
 * idle, but the server row stayed in its recording state forever, which is
 * where the ghost "Recording — open to check or finish" rows came from. A
 * doctor whose mic was busy got a note that claimed to be recording and no
 * error at all.
 */
async function acquireMicStream(): Promise<MediaStream> {
  // Always release any prior capture before re-acquiring the mic, so the
  // previous track is fully stopped first. Re-running getUserMedia while an old
  // track is still settling is what can hand back a muted track (silent audio).
  teardownCapture(capture)
  capture = null

  let newStream: MediaStream
  try {
    newStream = await navigator.mediaDevices.getUserMedia({ audio: true })
  } catch (e) {
    throw new Error(micErrorMessage(e))
  }

  // If the browser handed back a track that isn't live, bail now rather than
  // recording silence. (Guarded: jsdom / older browsers may not expose these.)
  const track = newStream.getAudioTracks?.()?.[0]
  if (track && track.readyState && track.readyState !== 'live') {
    newStream.getTracks().forEach((t) => t.stop())
    throw new Error('Microphone is not available — check it isn’t muted or in use by another app.')
  }

  return newStream
}

/** Release a stream acquired by `acquireMicStream` that we then couldn't use. */
function releaseMicStream(stream: MediaStream) {
  try {
    stream.getTracks().forEach((t) => t.stop())
  } catch {
    /* nothing useful to do — the stream is being discarded either way */
  }
}

function runCleanup(c: Capture) {
  const fns = c.cleanup.splice(0)
  for (const fn of fns) {
    try {
      fn()
    } catch {
      /* best-effort */
    }
  }
}

function startTimer(set: (s: Partial<RecordingStore>) => void) {
  if (timerInterval) clearInterval(timerInterval)
  timerInterval = setInterval(() => {
    const seconds = elapsedSeconds()
    set({ elapsed: seconds })
    if (hasReachedRecordingCeiling(seconds)) stopAtCeiling()
  }, 1000)
}

/**
 * A capture heartbeat answered (CLA-1564). If it named this clinician's other
 * quiet live recordings, that list becomes the run-on banner's; a response
 * without a list (an older server, a garbled body) changes nothing. Only the
 * live capture that is not already stopping may speak for the store — a late
 * answer to a finished capture's heartbeat must not raise a banner.
 */
function applyRunOnResponse(c: Capture, response: unknown) {
  if (capture !== c || c.stopBegun) return
  const list = parseOtherLiveRecordings(response)
  if (list === null) return
  const { runOnDismissed } = useRecordingStore.getState()
  useRecordingStore.setState({
    otherLiveRecordings: list,
    runOnDismissed: reconcileDismissed(runOnDismissed, list),
  })
}

/**
 * Finish the recording on the clinician's behalf, exactly as if they had
 * pressed Stop — the same `stopRecording`, so the audio is persisted,
 * committed and retried like any other consult — and tell the server why.
 * Used by the 6-hour ceiling (CLA-969) and the silence timeout (CLA-1564).
 *
 * Both are driven from timers that a background tab throttles (to once a
 * minute at worst in Chrome). That only makes the stop late, never wrong: each
 * checks a wall-clock measurement, not a count of ticks.
 *
 * Fires once. It only acts on a capture that is live, recording and not
 * already stopping; `stopRecording`'s own `stopBegun` guard covers a Stop — or
 * the other automatic stop — arriving in the same instant.
 */
function autoStop(reason: StoppedReason, why: string) {
  const c = capture
  if (!c || c.stopBegun) return
  if (useRecordingStore.getState().state !== 'recording') return
  stopTimer()
  console.warn(`[recording] note ${c.noteId} ${why} — stopping automatically`)
  useRecordingStore
    .getState()
    .stopRecording(reason)
    .catch((e) => console.error(`[recording] automatic stop (${reason}) failed`, e))
}

/** The recording has reached its 6-hour ceiling (CLA-969). */
function stopAtCeiling() {
  autoStop(
    STOPPED_REASON_DURATION_CEILING,
    `reached the recording ceiling at ${elapsedSeconds()}s`,
  )
}

/** Silence auto-stop poll (CLA-1564). One per live capture, like the level loop. */
let silenceInterval: ReturnType<typeof setInterval> | null = null

/**
 * One ~100 ms window from the live analyser, in dBFS — or null when it cannot
 * be judged: no Float32 read (older engines), or an AudioContext that is not
 * running (a suspended context hands back zeros that say nothing about the
 * room). Null resets the silence clock; it never counts as silence.
 */
function readWindowLevel(c: Capture, buf: Float32Array<ArrayBuffer>): number | null {
  const a = c.analyser
  if (!a || typeof a.getFloatTimeDomainData !== 'function') return null
  if (c.audioContext?.state !== 'running') return null
  a.getFloatTimeDomainData(buf)
  return rmsDbfs(buf)
}

function showSilenceWarning() {
  if (useRecordingStore.getState().silenceWarning) return
  useRecordingStore.setState({ silenceWarning: true })
  raiseSilenceAlert()
}

function hideSilenceWarning() {
  clearSilenceAlert()
  if (useRecordingStore.getState().silenceWarning) {
    useRecordingStore.setState({ silenceWarning: false })
  }
}

/**
 * Start measuring silence for the live capture (CLA-1564). Called wherever the
 * level loop starts — recording start, resume, the mic coming back — and it
 * restarts the clock each time: time on the far side of a pause or a lost mic
 * is never joined to time on this side.
 *
 * No analyser (Web Audio unavailable) means no silence auto-stop at all. That
 * is deliberate: without a meter there is nothing to measure, and guessing
 * would be the one way this could cut a consult.
 */
function startSilenceLoop() {
  const c = capture
  if (!c || silenceInterval) return
  c.silence.interrupt()
  if (!c.analyser) return
  const buf = new Float32Array(c.analyser.fftSize)
  silenceInterval = setInterval(() => {
    if (capture !== c) return
    const s = useRecordingStore.getState()
    if (s.state !== 'recording' || s.captureLost) return

    const verdict = c.silence.sample(Date.now(), readWindowLevel(c, buf))
    if (verdict === 'stop') {
      autoStop(STOPPED_REASON_SILENCE_TIMEOUT, 'has been silent for an hour')
    } else if (verdict === 'warn') {
      showSilenceWarning()
    } else if (s.silenceWarning && !c.silence.inSilence) {
      // Sound came back — the clinician is here. The prompt has done its job.
      hideSilenceWarning()
    }
  }, SILENCE_POLL_MS)
}

/**
 * Stop measuring (pause, mic lost, stop, discard). The clock restarts on the
 * next `startSilenceLoop`, and a standing prompt goes away — a pause or a stop
 * answers it.
 */
function stopSilenceLoop(c: Capture | null | undefined) {
  if (silenceInterval) {
    clearInterval(silenceInterval)
    silenceInterval = null
  }
  c?.silence.interrupt()
  hideSilenceWarning()
}

function stopTimer() {
  if (timerInterval) clearInterval(timerInterval)
  timerInterval = null
}

function stopLevelLoop() {
  if (levelInterval) {
    clearInterval(levelInterval)
    levelInterval = null
  }
  silentMs = 0
}

/**
 * The mic stopped delivering (CLA-1341). Freeze the timer at what was actually
 * captured and say so — loudly, because the previous behaviour was a timer
 * that kept climbing over a dead mic. Applies while recording or paused; a
 * track that dies during a pause is caught when resume finds the recorder
 * inactive. Idempotent, and ignores a capture that has already been replaced.
 */
function markCaptureLost(
  set: (s: Partial<RecordingStore>) => void,
  c: Capture,
  reason: CaptureLossReason,
  detectedBy: MicLossDetectedBy,
) {
  if (capture !== c) return
  const s = useRecordingStore.getState()
  if ((s.state !== 'recording' && s.state !== 'paused') || s.captureLost) return
  closeDurationSpan()
  stopTimer()
  stopLevelLoop()
  // Capture-lost time never counts as silence (CLA-1564).
  stopSilenceLoop(c)
  set({ captureLost: reason, audioWarning: false, elapsed: elapsedSeconds() })
  c.micLoss.lost(detectedBy, { recorderFailed: reason === 'error' })
}

/**
 * The track is delivering again (the phone was unlocked, the app foregrounded).
 * Only a `muted` loss can recover — an ended track or a failed recorder is
 * gone, and the recorder state says which. Restarts the clock from now, so
 * the suspended stretch is never counted.
 */
function markCaptureReturned(set: (s: Partial<RecordingStore>) => void, c: Capture) {
  if (capture !== c) return
  const s = useRecordingStore.getState()
  if (!s.captureLost) return
  if (c.recorder.state === 'inactive' || !trackIsLive(c.stream)) return
  if (s.state === 'recording' && c.recorder.state === 'recording') {
    openDurationSpan(false)
    startTimer(set)
    startLevelLoop(set)
    startSilenceLoop()
  }
  set({ captureLost: null })
  c.micLoss.recovered()
}

/** Why the track is not live, for the notice. */
function lossReason(c: Capture): CaptureLossReason {
  const track = c.stream.getAudioTracks?.()?.[0]
  if (c.recorder.state === 'inactive' || track?.readyState === 'ended') return 'ended'
  return 'muted'
}

/**
 * Take ownership of a stream and start recording into `noteData`.
 *
 * Returns false if it refused, in which case the caller still owns
 * `newStream` and must release it.
 *
 * ⚠ It refuses when a capture is already live (CLA-1459). This is the
 * invariant the start paths exist to protect and the one place it cannot be
 * bypassed: all three callers are start paths, and silently replacing
 * [capture] here is precisely what orphans a running recorder. The claim in
 * `claimStart` should mean this never fires; it is here so that a future
 * fourth caller cannot reintroduce the defect by forgetting to claim.
 */
function beginCapture(
  set: (s: Partial<RecordingStore>) => void,
  newStream: MediaStream,
  noteData: Note,
  appointmentContext: AppointmentContext | null,
  draftMadeForCapture: boolean,
): boolean {
  if (capture !== null) {
    console.warn(
      '[recording] refusing to start a second capture — one is already running for note ' +
        `${capture.noteId}; the request for note ${noteData.id} was dropped`,
    )
    return false
  }

  const supported = MIME_TYPE_PREFERENCE.find(
    (t) => MediaRecorder.isTypeSupported?.(t) ?? false,
  )
  const recorder = new MediaRecorder(newStream, {
    // Omit `mimeType` entirely when nothing matched — passing an unsupported
    // one throws, passing none lets the browser use its own default.
    ...(supported ? { mimeType: supported } : {}),
    audioBitsPerSecond: AUDIO_BITS_PER_SECOND,
  })
  const c: Capture = {
    recorder,
    stream: newStream,
    chunks: [],
    noteId: noteData.id,
    chunkIndex: 0,
    uploadChain: Promise.resolve(),
    progressive: true,
    consecutiveFailures: 0,
    stopping: false,
    stopBegun: false,
    draftMadeForCapture,
    createdTitle: noteData.title ?? '',
    title: null,
    silence: new SilenceClock(),
    stopReported: false,
    heartbeat: new CaptureHeartbeat(noteData.id, newStream, {
      onTrackChange: (live) => {
        if (live) markCaptureReturned(set, c)
        else markCaptureLost(set, c, lossReason(c), 'track_event')
      },
      // CLA-1564: what the silence clock hears right now, for the server to
      // tell this clinician's other devices about a recording gone quiet. Left
      // out when there is no meter — "not measured" is not "silent".
      silentMs: () => (c.analyser ? c.silence.silentMs(Date.now()) : null),
      onResponse: (response) => applyRunOnResponse(c, response),
    }),
    micLoss: new MicLossReporter(noteData.id, newStream),
    cleanup: [],
  }
  capture = c
  // Every exit runs the cleanup before it stops the tracks, so our own stop
  // can never read as a loss.
  c.cleanup.push(() => c.micLoss.stop())

  // The recorder failing outright is rare but silent — nothing else changes.
  recorder.onerror = () => markCaptureLost(set, c, 'error', 'recorder_error')

  // Coming back to the tab is the moment to check what happened while it was
  // hidden. Events that fired while the page was frozen may never have
  // reached us; the track's own state is the truth.
  if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
    const onVisibility = () => {
      if (document.visibilityState !== 'visible' || capture !== c) return
      if (c.recorder.state === 'inactive' || !trackIsLive(c.stream)) {
        markCaptureLost(set, c, lossReason(c), 'tab_return')
      } else {
        markCaptureReturned(set, c)
      }
    }
    document.addEventListener('visibilitychange', onVisibility)
    c.cleanup.push(() => document.removeEventListener('visibilitychange', onVisibility))
  }

  // Keep the screen on for the consult — the commonest way an iPhone browser
  // loses the mic is the 30-second auto-lock.
  c.cleanup.push(holdWakeLock())

  recorder.ondataavailable = (e) => {
    if (e.data.size === 0) return
    c.heartbeat.noteData()
    c.chunks.push(e.data)
    // Stream each chunk to the server during recording (CLA-465). Serialise via
    // the upload chain so chunks append in index order; a `gap` or error inside
    // appendChunk flips c.progressive and stop falls back to the full-blob upload.
    if (c.progressive) {
      const idx = c.chunkIndex++
      const data = e.data
      c.uploadChain = c.uploadChain.then(() => appendChunk(c, idx, data))
    }
  }

  recorder.start(CHUNK_MS)
  c.heartbeat.start()
  // Paired with `recording_stopped` (see `Capture.stopReported`). Sent here,
  // once the recorder is actually running, so a start that failed or was
  // refused never counts as a capture.
  trackClientEvent('recording_started', { platform: 'web' }, { note_id: noteData.id })

  // Client-side segmentation: buffers from t=0 on a stream clone; cancels
  // itself within seconds unless the server's segment_roller flag is true.
  segmentRoller?.cancel()
  const roller = new SegmentRoller(noteData.id, {
    // Only the current roller speaks for the store: a cancelled one reporting
    // late must not flip the flag for the recording that replaced it.
    onCapturingChange: (capturing) => {
      if (segmentRoller === roller) set({ backupCapturing: capturing })
    },
  })
  segmentRoller = roller
  set({ backupCapturing: false })
  roller.begin(newStream)

  // Best-effort live mic-level monitor (silent-failure guard).
  setupLevelMonitor(c, newStream)
  startLevelLoop(set)
  // Silence auto-stop (CLA-1564) — reads the same analyser.
  startSilenceLoop()

  set({
    state: 'recording',
    elapsed: 0,
    noteId: noteData.id,
    noteData,
    appointmentContext,
    audioWarning: false,
    captureLost: null,
    otherLiveRecordings: [],
    runOnDismissed: [],
  })
  openDurationSpan(true)
  startTimer(set)
  return true
}

export const useRecordingStore = create<RecordingStore>((set, get) => ({
  state: 'idle',
  elapsed: 0,
  error: null,
  audioWarning: false,
  captureLost: null,
  backupCapturing: false,
  noteId: null,
  noteData: null,
  generateResult: null,
  processingCancelled: false,
  currentNoteText: '',
  currentDoctorNotes: '',
  appointmentContext: null,
  pendingUploads: [],
  persistenceWarning: null,
  silenceWarning: false,
  otherLiveRecordings: [],
  runOnDismissed: [],

  startRecording: async (opts) => {
    // Refuse a new start until the previous consult has left `processing`
    // (F-1). The desktop widget used to offer the mic during processing, so a
    // back-to-back Record press could begin consult B while consult A's
    // generate POST was still in flight — and A's completion would then reset
    // the shared session out from under B. The mobile FAB already showed a
    // "Processing…" pill instead; this makes the store the backstop for both.
    if (get().state === 'recording' || get().state === 'paused' || get().state === 'processing') return
    // Synchronously, before any await — see `claimStart` (CLA-1459).
    const token = claimStart()
    if (token === null) return
    set({ error: null })

    set({ currentDoctorNotes: '' })

    try {
      // 1. Prove the microphone works BEFORE creating anything. A note created
      //    ahead of a failed getUserMedia is stranded server-side in its
      //    recording state with nothing to record into it.
      const stream = await acquireMicStream()
      if (!startIsCurrent(token)) {
        releaseMicStream(stream)
        return
      }

      // 2. Create the note draft
      const form = new FormData()
      form.append('note', '')
      form.append('title', '')
      if (opts?.profileId) {
        form.append('profile_id', String(opts.profileId))
      }
      if (opts?.patientUuid) {
        form.append('patient_uuid', opts.patientUuid)
      }
      let noteData: Note
      try {
        // Keyed so the client's gateway-error retry (a deploy mid-start)
        // returns the same draft instead of creating a second one.
        noteData = await api.postForm<Note>('/notes', form, {
          headers: { 'Idempotency-Key': crypto.randomUUID() },
        })
      } catch (e) {
        releaseMicStream(stream)
        const status = e instanceof ApiError ? e.status : 'unknown'
        set({ error: `Failed to create note (${status})` })
        return
      }

      // 3. Start capturing into it
      if (!startIsCurrent(token) || !beginCapture(set, stream, noteData, null, true)) {
        releaseMicStream(stream)
      }
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Microphone access required', state: 'idle', noteId: null, noteData: null })
    } finally {
      releaseStart(token)
    }
  },

  startConsultation: async (ctx: AppointmentContext) => {
    // See startRecording — a start is refused while a previous consult is still
    // processing so its completion can't clobber this one's session (F-1).
    if (get().state === 'recording' || get().state === 'paused' || get().state === 'processing') return
    const token = claimStart()
    if (token === null) return
    set({ error: null })

    set({ currentDoctorNotes: '' })

    try {
      // 1. Microphone first — see startRecording.
      const stream = await acquireMicStream()
      if (!startIsCurrent(token)) {
        releaseMicStream(stream)
        return
      }

      // 2. Create note with appointment link, patient pre-linked, clinical type mapped
      const form = new FormData()
      form.append('note', '')
      form.append('title', '')
      form.append('appointment_id', String(ctx.appointmentId))
      form.append('clinical_type', mapAppointmentTypeToClinicalType(ctx.appointmentType))
      if (ctx.patientUuid) {
        form.append('patient_uuid', ctx.patientUuid)
      }
      if (ctx.profileId) {
        form.append('profile_id', String(ctx.profileId))
      }
      let noteData: Note
      try {
        // Keyed so the client's gateway-error retry (a deploy mid-start)
        // returns the same draft instead of creating a second one.
        noteData = await api.postForm<Note>('/notes', form, {
          headers: { 'Idempotency-Key': crypto.randomUUID() },
        })
      } catch (e) {
        releaseMicStream(stream)
        const status = e instanceof ApiError ? e.status : 'unknown'
        set({ error: `Failed to create note (${status})` })
        return
      }

      // 3. Start capturing into it
      if (!startIsCurrent(token) || !beginCapture(set, stream, noteData, ctx, true)) {
        releaseMicStream(stream)
      }
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Microphone access required', state: 'idle', noteId: null, noteData: null, appointmentContext: null })
    } finally {
      releaseStart(token)
    }
  },

  startRecordingForNote: async (note: Note) => {
    // See startRecording — a start is refused while a previous consult is still
    // processing so its completion can't clobber this one's session (F-1).
    if (get().state === 'recording' || get().state === 'paused' || get().state === 'processing') return
    // currentDoctorNotes is reset here; the typed-then-record seed happens in
    // NoteDetail from the *live* editor text (CLA-772), since note.note may not
    // have autosaved yet when the doctor hits record.
    const token = claimStart()
    if (token === null) return
    set({ error: null, currentDoctorNotes: '' })

    try {
      // The note already exists here, so there is nothing to strand — but the
      // mic still has to be proven before the recorder is wired up.
      const stream = await acquireMicStream()
      if (!startIsCurrent(token) || !beginCapture(set, stream, note, null, false)) {
        releaseMicStream(stream)
      }
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Microphone access required', state: 'idle' })
    } finally {
      releaseStart(token)
    }
  },

  pauseRecording: () => {
    // The roller records its own CLONE of the mic, so pausing the main
    // recorder does not pause it (CLA-1552). Pause it first and whatever the
    // main recorder's state: a pause is the clinician saying "stop listening",
    // and the roller must honour that even if the main recorder has already
    // died under it. Idempotent.
    segmentRoller?.pause()
    if (capture?.recorder.state === 'recording') {
      capture.recorder.pause()
      capture.heartbeat.setState('paused')
      closeDurationSpan()
      stopTimer()
      // Pause the silence monitor and clear any standing warning so a pause
      // isn't itself read as silence.
      stopLevelLoop()
      // Paused time never counts toward the silence auto-stop (CLA-1564).
      stopSilenceLoop(capture)
      set({ state: 'paused', audioWarning: false })
    }
  },

  resumeRecording: () => {
    if (!capture) return
    if (capture.recorder.state === 'inactive') {
      // The track died during the pause and took the recorder with it. A
      // silent no-op here is the worst answer — the clinician tapped resume
      // and nothing happened. Say what is going on instead (CLA-1341).
      markCaptureLost(set, capture, lossReason(capture), 'resume')
      return
    }
    if (capture.recorder.state === 'paused') {
      const c = capture
      c.recorder.resume()
      // In lockstep with the main recorder (CLA-1552): resume exactly when it
      // does — including onto a muted track below, where both are recording
      // whatever the track delivers — and never on the dead-recorder branch
      // above, where the clinician's pause still stands.
      segmentRoller?.resume()
      c.heartbeat.setState('recording')
      if (get().captureLost || !trackIsLive(c.stream)) {
        // Resuming onto a muted track: stay in the lost state with the clock
        // frozen until the track actually delivers again.
        if (!get().captureLost) c.micLoss.lost('resume')
        set({ state: 'recording', captureLost: get().captureLost ?? lossReason(c) })
        return
      }
      openDurationSpan(false)
      startTimer(set)
      set({ state: 'recording' })
      // Resume the silence monitor (the analyser stays connected across pause).
      startLevelLoop(set)
      // …and the silence auto-stop clock, from zero (CLA-1564).
      startSilenceLoop()
    }
  },

  stopRecording: async (reason) => {
    const active = capture
    if (!active) return
    // One stop per capture (CLA-969) — see `Capture.stopBegun`. Synchronous,
    // before any await, or two stops both get past it.
    if (active.stopBegun) return
    active.stopBegun = true
    // Validated, not trusted: a caller wiring this straight to an `onClick`
    // would otherwise report a MouseEvent as the reason.
    const stoppedReason = toStoppedReason(reason)

    // Close the accounting span BEFORE the awaits below, so the duration we
    // report is when the clinician stopped — not when the chunk drain and
    // segment finalisation happened to finish (CLA-1267).
    closeDurationSpan()
    // Tell the detector this silence is deliberate before the drain below
    // takes its time (CLA-1342).
    active.heartbeat.setState('stopping')
    const elapsed = elapsedSeconds()
    const { noteId, currentDoctorNotes } = get()
    const userNotes = currentDoctorNotes?.trim() ?? ''
    // F-1 — this stop runs across several awaits (chunk drain, segment finish,
    // the generate POST). If a newer recording takes the store over in that
    // window, every completion `set()` below that resets `state`/`noteId`/
    // `noteData` would tear the newer session down. `stillOwns()` gates those
    // resets: when the store no longer points at THIS recording's note, the
    // completion does its own bookkeeping (persisted-entry status, banner) but
    // leaves the shared session alone. The start* guards make a same-tab
    // takeover unreachable today; this is the store-level backstop that keeps
    // the invariant true regardless of how a takeover arrives.
    //
    // ⚠ A takeover is a DIFFERENT note owning the store — not a cleared one.
    // NotesView's WS handler calls `clearNoteData()` (noteId → null) the
    // moment the server broadcasts `note_changed`/`note_generated`, and the
    // server writes the note BEFORE it answers the generate POST, so on a
    // healthy commit the clear lands a few hundred ms ahead of the response.
    // `get().noteId === noteId` then read that as a takeover, every completion
    // below returned without touching `state`, and the watchdog (which made
    // the same comparison) bailed on the same null. Prod note 22146,
    // 2026-09-17: generate → 200 in 15 s, note on screen, spinner forever.
    // `processing` can only be entered here and every stop bumps the epoch,
    // so "still processing on my epoch" is this consult's session whatever
    // happened to `noteId` in the meantime.
    const epoch = ++processingEpoch
    const stillOwns = () =>
      get().noteId === noteId || (get().state === 'processing' && processingEpoch === epoch)
    set({
      state: 'processing',
      processingCancelled: false,
      audioWarning: false,
      // The run-on banner belongs to a live capture (CLA-1564).
      otherLiveRecordings: [],
      runOnDismissed: [],
      captureLost: null,
      // CLA-938 — the warning is about THIS recording's backup, so it must not
      // outlive the stop that produced it.
      persistenceWarning: null,
    })
    // CLA-1359 — arm the watchdog as the state is entered, not around the
    // commit specifically: anything below that hangs (the chunk drain, the
    // segment finish, the commit itself) strands the UI identically, and the
    // clinician's experience is the same whichever await it was.
    clearProcessingWatchdog()
    processingWatchdog = setTimeout(() => {
      void reconcileStuckProcessing(epoch, noteId, get, set)
    }, PROCESSING_WATCHDOG_MS)
    // CLA-594 — a per-recording profile choice ends with the recording. The
    // note already carries it (applied at create), so dropping it here just
    // returns the next consult to the clinician's account default rather than
    // silently inheriting a one-off choice made hours ago.
    useRecordingPrefs.getState().clearOverride()
    stopTimer()
    runCleanup(active)
    // Stop the live level monitor + release its AudioContext. This also ends
    // the silence clock and puts away any silence prompt (CLA-1564).
    teardownLevelMonitor(active)
    reportRecordingStopped(active, active.noteId, elapsed, stoppedReason)
    // The jottings go out as user_notes at generate — drop any pending live PATCH.
    clearDoctorNotesSave()

    // Stop and collect the final chunk before reading the buffer. Bounded:
    // a recorder whose track the OS suspended (CLA-1341) may never deliver
    // `stop`, and the chunks already in hand are the recording — waiting
    // forever here would turn "finish with what was captured" into a hang.
    //
    // CLA-1432: bounded must not mean discarded. This wait used to resolve at
    // `STOP_SETTLE_MS`, detach `ondataavailable` and freeze the Blob, so a
    // final chunk arriving a second later had nowhere to go — and the
    // shortened recording was then committed as complete, because the
    // integrity hash describes what the client assembled rather than whether
    // the client assembled all of it. `settleRecorderStop` keeps the buffer
    // owned for the whole of finalization and says how it ended.
    const startedFinalizingAt = Date.now()
    const finalization = await settleRecorderStop(active)
    const finalizeMs = Date.now() - startedFinalizingAt

    active.heartbeat.stop()
    active.recorder.onerror = null
    active.stream.getTracks().forEach((t) => t.stop())
    // Read the buffer only now, with nothing left that can add to it. A chunk
    // that somehow arrives after this is reported rather than dropped on the
    // floor (see `reportLateTail`) — silence is what made this class of loss
    // invisible for as long as it was.
    const blob = new Blob(active.chunks, { type: 'audio/webm' })
    const frozenChunkCount = active.chunks.length
    active.recorder.ondataavailable = (e) => reportLateTail(active, e, noteId)
    capture = null

    if (finalization === 'unconfirmed') {
      // The recorder never answered. We finish with what was captured — that
      // was always right — but this recording is NOT certified complete, and
      // saying so is the whole point: an unreported truncation is
      // indistinguishable from a short consult.
      console.warn(
        `[recording] finalization unconfirmed after ${finalizeMs}ms — committing ${blob.size} bytes in ${frozenChunkCount} chunks; the tail may be missing`,
      )
      trackClientEvent('recording_finalize_unconfirmed', {
        note_id: noteId ?? 0,
        bytes: blob.size,
        chunks: frozenChunkCount,
        elapsed_seconds: elapsed,
        waited_ms: finalizeMs,
      })
    } else if (finalizeMs > STOP_SETTLE_MS) {
      // Answered, but late. Worth a line: under the old bound this recording
      // would have been truncated, so these are the consults the fix saved.
      console.warn(`[recording] recorder finalized late (${finalizeMs}ms) — tail retained`)
    }

    // Guard: a muted / dead mic captures a full-length but silent recording.
    // Detect it locally and let the doctor re-record straight away, rather than
    // uploading silence and surfacing a red error ~90s later (F-1005). The note
    // stays selected so they can record again on it.
    //
    // ⚠ This branch DISCARDS the audio — it returns before the recording is
    // persisted. So it only ever runs on a SHORT recording stopped by the
    // clinician (CLA-969, `shouldCheckForSilence`): a long recording, or one
    // the ceiling stopped, is saved and committed whatever it sounds like, and
    // is never decoded whole in the browser. Zero chunks is different — there
    // is no audio to lose — and stays unconditional.
    const checkSilence = shouldCheckForSilence({
      recordedSeconds: elapsed,
      bytes: blob.size,
      stoppedReason,
    })
    if (active.chunks.length === 0 || (checkSilence && (await isEffectivelySilent(blob)))) {
      segmentRoller?.cancel()
      segmentRoller = null
      if (get().processingCancelled) {
        if (stillOwns()) set({ state: 'idle', elapsed: 0, noteId: null, noteData: null, processingCancelled: false, currentNoteText: '', appointmentContext: null })
        return
      }
      if (stillOwns()) {
        set({
          error: 'No sound was captured — check your microphone isn’t muted or in use by another app, then record again.',
          state: 'idle',
          elapsed: 0,
        })
      }
      return
    }

    // ── Durable persistence (CLA-938) ───────────────────────────────────────
    // The key is minted HERE — above the persistence write, not merely above the
    // commit. It used to be generated after `await active.uploadChain`, which is
    // too late to persist: an entry written without its key means a later retry
    // mints a fresh UUID and the server writes a SECOND note row for one
    // consult. Everything a retry needs (key, noteId, jottings, the full blob)
    // goes to disk before the first commit is attempted, because a commit that
    // fails takes the in-memory blob with it.
    const idempotencyKey = crypto.randomUUID()
    const saved = await savePendingRecording({
      idempotencyKey,
      noteId,
      userNotes,
      audio: blob,
      durationSeconds: elapsed,
      // Persisted so a retry — manual or the background scheduler — tells the
      // server the same thing the first attempt did (CLA-969).
      stoppedReason,
    })
    const pendingId = saved.ok ? saved.id : null
    if (!saved.ok) {
      // No safety net (private browsing, blocked storage, or out of quota). Say
      // so — the clinician needs to know this is the one case where closing the
      // tab really does lose the consult. The upload still proceeds; a missing
      // backup is a reason to warn, never a reason to abandon the recording.
      set({ persistenceWarning: saved.message })
    }

    // Wait for any in-flight chunk appends to finish, then commit. If every
    // chunk landed in order (progressive still true) the audio is already on the
    // server — send only its SHA-256 + size for the integrity gate, no re-upload.
    // If a chunk gapped or errored, fall back to the atomic full-blob upload.
    // Stop any in-flight chunk retry from holding up the stop path (CLA-945
    // Phase 3). Retries earn their keep mid-recording; at stop they are pure
    // latency in front of the clinician.
    active.stopping = true
    try {
      await active.uploadChain
    } catch {
      active.progressive = false
    }

    // Land the tail segment (final=1) before generate so the server sees the
    // assembly converging (bounded inside finish(); the whole-file paths below
    // carry the note regardless of the outcome here).
    if (segmentRoller) {
      await segmentRoller.finish()
      segmentRoller = null
    }

    // The Idempotency-Key (minted above, before the persistence write) makes any
    // deploy-window 502 retry (api.client retries up to 3×) dedup against the
    // same server-side note row instead of creating duplicates. Mirrors the iOS
    // native path and the AudioImportSheet flow (CLA-593). Reused across a
    // progressive→atomic fallback, and — since CLA-938 — across every later
    // retry from the persisted entry too.

    // Shared generate form. Canonical capture (CLA-772): the doctor's jottings go
    // out as "Your notes" (→ doctor_notes), never the body. `audio` (atomic) or
    // `audio_sha256`+`audio_size` (progressive) is added per-mode below.
    const buildForm = (): FormData => buildGenerateForm(noteId, userNotes, stoppedReason)

    // The whole-blob commit, chunked when the recording is big enough that one
    // request would risk the edge body cap (CLA-1243).
    const commitWhole = (): Promise<Note> =>
      commitFullUpload(blob, noteId, userNotes, idempotencyKey, stoppedReason)

    // Upload — POST /api/v1/notes/generate with existing_note_id
    try {
      let data: Note
      try {
        if (active.progressive) {
          const form = buildForm()
          form.append('audio_sha256', await sha256Hex(blob))
          form.append('audio_size', String(blob.size))
          // The progressive commit can answer with the note OR a bare
          // {status:"reupload_required"} (Note.status has no such member), so
          // widen the response type to narrow on it.
          const result = await api.postForm<Note | { status: 'reupload_required' }>(
            '/notes/generate',
            form,
            { headers: { 'Idempotency-Key': idempotencyKey } },
          )
          // Integrity gate failed (assembled file ≠ local file) — fall back to
          // the proven atomic upload of the full blob.
          data = result.status === 'reupload_required' ? await commitWhole() : result
        } else {
          data = await commitWhole()
        }
      } catch (e) {
        // Plan note-cap (CLA-847): surface the server's message, not "Upload
        // failed" — the audio wasn't lost, the plan is out of included notes.
        //
        // CLA-938 splits this branch. The cap and a genuine transport failure
        // used to share one `set()`, which is wrong now that a failure keeps the
        // audio: both retain the persisted entry, but only the transport case
        // gets a "Retry upload" button. Retrying a 402 just 402s again until the
        // plan changes, so the cap entry is parked as `plan_capped` and the UI
        // prompts an upgrade instead of an action that cannot work.
        //
        // CLA-1243 adds a third: the audio itself is unreadable. Same shape as
        // the cap — keep the row, offer no retry — but the opposite cause, and
        // it must not be reported as "Upload failed (unknown)", which is the
        // message that made the 2026-09-04 incident impossible to read.
        const capMessage = noteCapMessage(e)
        const unreadable = e instanceof RecordingUnreadableError
        const notAccepted = isAudioNotAccepted(e)
        const status = e instanceof ApiError ? e.status : 'unknown'
        const message = unreadable
          ? 'This recording can no longer be read from this browser.'
          : notAccepted
            ? 'This note is still finishing an earlier recording. Yours is saved on this device — retry in a moment.'
            : (capMessage ?? `Upload failed (${status})`)

        // An explicit user cancel outranks the failure: cancelProcessing means
        // "I don't want this consult", so the audio goes rather than lingering
        // in the recovery banner. Same call the success path makes below.
        if (get().processingCancelled) {
          if (pendingId != null) await deletePendingRecording(pendingId)
          if (stillOwns()) set({ state: 'idle', elapsed: 0, noteId: null, noteData: null, processingCancelled: false, currentNoteText: '', appointmentContext: null })
          return
        }

        if (pendingId != null) {
          const parked = unreadable ? 'unreadable' : capMessage ? 'plan_capped' : 'pending'
          await updatePendingStatus(pendingId, parked, {
            lastError: message,
            incrementAttempts: true,
          })
        }
        // Only surface the error / reset the session if THIS recording still
        // owns the store — otherwise a newer consult is live and its session
        // must not be flipped to idle (F-1). The persisted entry above keeps the
        // audio recoverable either way.
        if (stillOwns()) set({ error: message, state: 'idle', elapsed: 0, noteId: null, noteData: null, currentNoteText: '', appointmentContext: null })
        await get().recoverPendingUploads()
        return
      }

      // Check if cancelled while the request was in flight. The commit
      // succeeded, so the server has the audio — deleting the local copy on an
      // explicit cancel is correct, not a loss.
      if (get().processingCancelled) {
        if (pendingId != null) await deletePendingRecording(pendingId)
        if (stillOwns()) set({ state: 'idle', elapsed: 0, noteId: null, noteData: null, processingCancelled: false, currentNoteText: '', appointmentContext: null })
        return
      }

      // Committed. This is the ONLY place the live path deletes audio, and it
      // runs on a confirmed 2xx with a note body — never on an assumption that
      // the upload probably worked.
      if (pendingId != null) await deletePendingRecording(pendingId)

      // A newer consult has taken the store over (F-1) — the note is committed
      // server-side and will surface via the notes list / WS, so deliver
      // nothing into the shared session and leave the live recording untouched.
      if (!stillOwns()) return

      if (data.status !== 'processing' && data.note && data.note.trim() !== '') {
        // Synchronous response (note already generated) — show immediately.
        // The AI is always the body now; "Your notes" surfaces from doctor_notes
        // (the DoctorNotesCard), so there is no suggestion to merge.
        set({
          generateResult: {
            noteId: noteId!,
            noteText: data.note,
            transcript: data.transcript || '',
            detectedTreatments: data.detected_treatments || [],
            isSuggestion: false,
            doctorText: '',
          },
          state: 'idle',
          elapsed: 0,
          noteId: null,
          noteData: null,
          currentNoteText: '',
          currentDoctorNotes: '',
          appointmentContext: null,
        })
      } else {
        // Async response (processing in background) — keep note selected,
        // update noteData with the response so the detail pane shows processing state
        set({
          state: 'idle',
          elapsed: 0,
          noteData: data,
          currentNoteText: '',
          currentDoctorNotes: '',
        })
      }
    } catch {
      // Anything the inner handler didn't catch (a throw from the success-path
      // bookkeeping itself). Whether the server committed is unknown from here,
      // so the persisted entry is RETAINED and offered for retry — the original
      // Idempotency-Key means a retry against an upload that did land dedups
      // rather than duplicating. Unknown is not the same as failed, but audio
      // kept unnecessarily is recoverable and audio dropped is not.
      if (pendingId != null) {
        await updatePendingStatus(pendingId, 'pending', {
          lastError: 'Network error uploading recording',
          incrementAttempts: true,
        })
      }
      // Don't reset the session if a newer consult now owns the store (F-1);
      // the persisted entry above keeps this recording recoverable.
      if (stillOwns()) set({ error: 'Network error uploading recording', state: 'idle', elapsed: 0, noteId: null, noteData: null, currentNoteText: '', appointmentContext: null })
      await get().recoverPendingUploads()
    }
  },

  /**
   * Retry a persisted recording's commit (CLA-938 Phase 1 — user-initiated;
   * automatic backoff retry is Phase 2).
   *
   * Always ATOMIC, always with the recording's ORIGINAL Idempotency-Key. See
   * `commitAtomicUpload` for both halves of the reasoning: progressive state
   * cannot survive the end of a capture, and the stable key is what makes
   * re-sending a possibly-partially-committed recording safe.
   */
  retryUpload: async (pendingId: number) => {
    const row = await getPendingRecording(pendingId)
    if (!row) {
      // Already gone — another tab drained it, or it was evicted. Re-read rather
      // than leaving a stale row on screen that retries into nothing.
      await get().recoverPendingUploads()
      return
    }

    retryingIds.add(pendingId)
    await updatePendingStatus(pendingId, 'uploading')
    await get().recoverPendingUploads()

    try {
      await commitFullUpload(
        row.audio,
        row.noteId,
        row.userNotes,
        row.idempotencyKey,
        // A row written before CLA-969 has no field: a normal stop.
        toStoppedReason(row.stoppedReason),
      )
      // Confirmed commit — the only condition under which a retry deletes audio.
      await deletePendingRecording(pendingId)
      toast.success(
        'Recording uploaded',
        'The note is generating now and will appear in your list.',
      )
    } catch (e) {
      const capMessage = noteCapMessage(e)
      const unreadable = e instanceof RecordingUnreadableError
      // CLA-1430. This is the likeliest place a clinician meets the refusal:
      // they press Retry while the note is still finishing an earlier
      // recording. Their audio is safe and the next retry will take it, so
      // this must not arrive as a red "Upload failed".
      const notAccepted = isAudioNotAccepted(e)
      const status = e instanceof ApiError ? e.status : 'unknown'
      const message = unreadable
        ? 'This recording can no longer be read from this browser.'
        : notAccepted
          ? 'This note is still finishing an earlier recording. Yours is safe here — retry in a moment.'
          : (capMessage ?? `Upload failed (${status})`)
      const parked = unreadable ? 'unreadable' : capMessage ? 'plan_capped' : 'pending'
      await updatePendingStatus(pendingId, parked, {
        lastError: message,
        incrementAttempts: true,
      })

      if (notAccepted) {
        toast.info('Not uploaded yet', message)
      } else {
        toast.error(
          unreadable ? 'Recording unavailable' : 'Upload failed',
          unreadable ? `${message} Nothing further can be uploaded for it.` : message,
        )
      }
    } finally {
      // Always clear the in-flight mark before the final re-read, or the button
      // stays disabled forever on a failed retry.
      retryingIds.delete(pendingId)
      await get().recoverPendingUploads()
    }
  },

  discardPendingUpload: async (pendingId: number) => {
    await deletePendingRecording(pendingId)
    await get().recoverPendingUploads()
  },

  recoverPendingUploads: async () => {
    const rows = await getPendingRecordings()
    set({
      pendingUploads: rows
        .map(toPendingUpload)
        .filter((p): p is PendingUpload => p !== null)
        // `retrying` is derived from the live in-flight set, never carried over
        // from the previous projection — copying the old flag forward left a
        // failed retry stuck showing "Uploading…" with its button disabled.
        .map((p) => ({ ...p, retrying: retryingIds.has(p.id) })),
    })
  },

  clearPersistenceWarning: () => {
    set({ persistenceWarning: null })
  },

  dismissRunOnBanner: () => {
    const ids = get().otherLiveRecordings.map((r) => r.note_id)
    set({ runOnDismissed: [...new Set([...get().runOnDismissed, ...ids])] })
  },

  keepRecordingThroughSilence: () => {
    capture?.silence.rearm()
    hideSilenceWarning()
  },

  cancelRecording: async () => {
    if (await get().confirmDiscardRecording()) void discardLiveRecording()
  },

  confirmDiscardRecording: async (opts) => {
    const before = get()
    const live = before.state === 'recording' || before.state === 'paused'
    // What the discard will take: the draft only if this recording made it
    // and nothing was added to it (CLA-1875, CLA-1880). Read before the
    // dialog, from the capture that is live now; the dialog is modal, so
    // nothing can be typed between this and the discard.
    const withNote = opts?.withNote ?? discardTakesDraft(capture)
    // Pause while the clinician decides, so nothing is captured into a
    // recording that may be thrown away. Only a pause made here is undone.
    const pausedHere = before.state === 'recording'
    if (pausedHere) get().pauseRecording()
    const { title, body } = discardRecordingCopy({
      length: live ? formatElapsed(get().elapsed) : null,
      patientName:
        opts?.patientName ?? before.noteData?.patient?.display_name ?? before.appointmentContext?.patientName ?? null,
      withNote,
    })
    const ok = await confirmDialog(title, {
      description: body,
      confirmLabel: 'Discard',
      cancelLabel: 'Keep recording',
      variant: 'danger',
    })
    if (!ok && pausedHere && get().state === 'paused') get().resumeRecording()
    return ok
  },

  /**
   * Stop recording without confirmation and keep the draft — used when the
   * note itself is being discarded (the caller posts `/discard`) and as test
   * cleanup. Discarding a recording the clinician chose to throw away is
   * `cancelRecording`, which also tells the server to forget the recording
   * and discards an untouched draft the recording created (CLA-1880).
   */
  forceStopRecording: () => {
    closeDurationSpan()
    stopTimer()
    clearDoctorNotesSave()
    // A discard still ends the capture, so it closes the started/stopped pair
    // — otherwise every discarded recording would read as an orphan.
    if (capture) reportRecordingStopped(capture, capture.noteId, elapsedSeconds(), 'discarded')

    // Detach handlers + stop + release tracks in one shot, so a late chunk from
    // this discarded recorder can never reach the next recording's buffer.
    teardownCapture(capture)
    capture = null
    segmentRoller?.cancel()
    segmentRoller = null

    // Stop the recording and drop the audio held here. The server draft is
    // left alone: the note-discard callers post `/discard` themselves, and
    // `cancelRecording` posts `abandon_capture` (and `/discard` when the
    // draft goes too).
    set({ state: 'idle', elapsed: 0, error: null, captureLost: null, backupCapturing: false, noteId: null, noteData: null, currentNoteText: '', appointmentContext: null, otherLiveRecordings: [], runOnDismissed: [] })
  },

  cancelProcessing: () => {
    segmentRoller?.cancel()
    segmentRoller = null
    // Set flag so the in-flight fetch result is ignored when it resolves
    set({ processingCancelled: true, state: 'idle', elapsed: 0, noteId: null, noteData: null, currentNoteText: '', appointmentContext: null })
  },

  dismissError: () => {
    set({ error: null })
  },

  clearGenerateResult: () => {
    set({ generateResult: null })
  },

  setGenerateResult: (result) => {
    set({ generateResult: result })
  },

  clearNoteData: () => {
    set({ noteId: null, noteData: null })
  },

  setCurrentNoteText: (text: string) => {
    set({ currentNoteText: text })
  },

  setCurrentNoteTitle: (noteId, title) => {
    if (capture && capture.noteId === noteId) capture.title = title
  },

  setCurrentDoctorNotes: (text: string) => {
    set({ currentDoctorNotes: text })
    // Live durability (CLA-772 / mirrors iOS CLA-764): debounce-PATCH the
    // jottings to the draft's `doctor_notes` so they survive a reload/crash
    // mid-recording and reflect on other devices (`update_draft` broadcasts
    // doctor_notes). The value is also sent as `user_notes` at generate, so a
    // missed save self-heals; an empty value clears it server-side (= dismiss).
    const ref = get().noteData?.uuid
    if (!ref) return
    clearDoctorNotesSave()
    doctorNotesSaveTimer = setTimeout(() => {
      doctorNotesSaveTimer = null
      api.patch(`/notes/${ref}`, { doctor_notes: text }).catch(() => {})
    }, 800)
  },
}))

/**
 * Whether the clinician put anything into this capture's note while it
 * recorded: "Your notes", a title, or the consult pad. The body is not
 * editable during a recording (CLA-772 Decision A), and a patient or clinic
 * link is not something typed. John, 2026-10-22: once the server can take the
 * recording off a note (CLA-1880), keep the note whenever the clinician typed
 * something into it.
 */
function clinicianAddedToNote(c: Capture): boolean {
  if (useRecordingStore.getState().currentDoctorNotes.trim()) return true
  const title = c.title?.trim() ?? ''
  if (title !== '' && title !== c.createdTitle.trim()) return true
  return consultPadHasContent(c.noteId)
}

/**
 * Whether discarding this recording discards its note as well: only a draft
 * the recording created (CLA-1875) that the clinician added nothing to
 * (CLA-1880). Everything else keeps the note and loses only the recording.
 */
function discardTakesDraft(c: Capture | null): boolean {
  return !!c?.draftMadeForCapture && !clinicianAddedToNote(c)
}

/** How long a discard waits for the capture's last writes before abandoning. */
const DISCARD_SETTLE_MS = 5_000

/**
 * Let the capture's writes that are already on the wire land (or fail), so
 * nothing streamed for this recording reaches the note after the server has
 * forgotten it: the chunk append, heartbeat and live segment in flight.
 * Nothing new is sent (the caller has stopped the stream and torn the capture
 * down), queued chunks and segments are dropped, and no retry waits. Bounded:
 * a hung request must not hold the abandon back for long. A chunk that lands
 * later still cannot extend the file: the server rewound its cursor to 0.
 */
async function settleCaptureWrites(c: Capture, roller: SegmentRoller | null): Promise<void> {
  const writes = Promise.all([c.uploadChain, c.heartbeat.settled(), roller?.drained()]).catch(() => undefined)
  await Promise.race([writes, new Promise((r) => setTimeout(r, DISCARD_SETTLE_MS))])
}

/**
 * `POST /notes/:ref/abandon_capture` (claro-elixir CLA-1880): the server drops
 * the recording's streamed audio, live segments and `recording` stage and
 * leaves the note's content alone. `committed` is its 409, the recording was
 * already sent for transcription; `unavailable` is anything else that went
 * wrong (offline, a 404 from a server without the endpoint, a 5xx).
 */
async function abandonCapture(ref: string | number): Promise<'abandoned' | 'committed' | 'unavailable'> {
  try {
    await api.post(`/notes/${ref}/abandon_capture`)
    return 'abandoned'
  } catch (e) {
    return e instanceof ApiError && e.status === 409 ? 'committed' : 'unavailable'
  }
}

/**
 * Discard a live recording (CLA-1875, CLA-1880). In order:
 *
 * 1. Stop the stream: the capture is torn down, queued chunks and segments
 *    are dropped and the heartbeat stops, then the writes already in flight
 *    are let land (`settleCaptureWrites`).
 * 2. `abandon_capture`: the server forgets the recording and keeps the note.
 *    Before it, a discarded recording stayed on the note: a draft stayed
 *    `recording` with the streamed audio, shown as a live consult, and
 *    `FinalizeAbandonedProgressiveStreams` transcribed the audio the clinician
 *    had discarded into it (AU prod note 25361, 2026-10-12).
 * 3. `/discard` the note too only when this recording created it and the
 *    clinician added nothing to it (`discardTakesDraft`). It stays
 *    restorable (`POST /notes/:id/restore`). A note recorded into, or a draft
 *    with typed notes, a title or pad content, is kept, without the recording.
 *
 * A 409 means the recording was already committed: the note is left alone.
 * If `abandon_capture` fails any other way the interim behaviour (#719)
 * applies: a draft the recording created is discarded whatever it holds, so
 * the sweep cannot transcribe the discarded audio into it, and a note
 * recorded into is left alone with the audio still on its server copy.
 *
 * Only while the capture is still live. Once a stop has begun (the clinician,
 * the ceiling, silence) the audio is being committed into the note; the
 * discard then does nothing at all. That is read AFTER the confirmation, so a
 * stop that wins while the clinician is deciding is never touched.
 *
 * Best effort and never blocking: the recorder is idle before the first
 * request goes out, and a failed `/discard` leaves the draft to the server's
 * sweeps, which is where it was before.
 */
async function discardLiveRecording(): Promise<void> {
  // The stop won: it owns the capture and the note. Tearing the capture down
  // under it would also cut the audio it is about to commit.
  if (capture?.stopBegun) return
  const live = capture
  const { noteData, currentDoctorNotes } = useRecordingStore.getState()
  const noteUuid = live && noteData?.id === live.noteId && noteData.uuid ? noteData.uuid : null
  // Decided now, as the dialog that was just answered said.
  const takesDraft = discardTakesDraft(live)
  // "Your notes" typed in the last moments may not have been saved yet; the
  // stop below cancels that save, so a kept note gets it sent here.
  const unsavedDoctorNotes = doctorNotesSaveTimer !== null ? currentDoctorNotes : null
  const roller = segmentRoller
  if (live) {
    // Queued chunks go nowhere and the one in flight is not retried.
    live.progressive = false
    live.stopping = true
  }
  useRecordingStore.getState().forceStopRecording()
  if (!live) return
  const ref = noteUuid ?? (live.noteId > 0 ? live.noteId : null)
  if (ref === null) return

  await settleCaptureWrites(live, roller)
  const outcome = await abandonCapture(ref)
  // The interim rule when the server could not forget the recording.
  const discardNote = outcome === 'abandoned' ? takesDraft : outcome === 'unavailable' && live.draftMadeForCapture

  if (!discardNote) {
    if (unsavedDoctorNotes !== null) api.patch(`/notes/${ref}`, { doctor_notes: unsavedDoctorNotes }).catch(() => {})
    if (outcome === 'abandoned') {
      // It is no longer `recording`, and the server broadcasts nothing.
      const detail: RecordingCaptureAbandonedDetail = { noteId: live.noteId, noteUuid }
      window.dispatchEvent(new CustomEvent(RECORDING_CAPTURE_ABANDONED, { detail }))
    }
    return
  }
  const detail: RecordingDraftDiscardedDetail = { noteId: live.noteId, noteUuid }
  api
    .post(`/notes/${ref}/discard`)
    .then(() => window.dispatchEvent(new CustomEvent(RECORDING_DRAFT_DISCARDED, { detail })))
    .catch(() => {})
}

export function formatElapsed(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}
